'use client'

import { useState, useRef, useEffect, useMemo } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import {
  Search,
  Camera,
  Car,
  ShoppingBag,
  User,
  ShieldCheck,
  MessageCircle,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  Filter,
  CheckCircle2,
  Lock,
  X,
  Globe,
  Plus,
  Trash2,
  Clock,
  MapPin,
  Send,
  Loader2,
  AlertCircle,
  Package,
  SlidersHorizontal,
  LogOut,
  Info,
  Mic,
  Volume2,
  Paperclip,
  Check,
  Home,
  ScanLine,
  TrendingUp,
  Star,
  CheckCircle,
  Sliders,
  Wrench,
  Layers,
  Truck,
  ExternalLink,
  Bell,
  Heart,
  Store,
  CreditCard,
  Compass,
  Navigation
} from 'lucide-react'
import { authClient, subscribeToAuth } from '@/lib/auth'
import LoginForm from '@/components/auth/LoginForm'
import { VerifyEmailDialog } from '@/components/VerifyEmailDialog'
import {
  GLOBAL_MAKES,
  YEARS,
  DEFAULT_TRIMS,
  DEFAULT_ENGINES,
  ENGINE_CONFIGURATIONS,
  MAKE_TRIMS,
  MMYTE_CATEGORIES,
  MMYTE_KEYWORDS,
  checkFitmentCompatibility,
  CategoryData
} from '@/lib/catalog'
import { resolveProductSku } from '@/lib/sku'
import dynamic from 'next/dynamic'
import { getUserOrders, Order } from '@/lib/marketplace'
import ProductCard from '@/components/ProductCard'
import { FilterState, WEST_AFRICA_HUBS } from '@/components/MarketplaceFilterDrawer'
import BrandLogo from '@/components/BrandLogo'
import VehicleBrandLogo from '@/components/VehicleBrandLogo'
import CategoryIcon from '@/components/CategoryIcon'
import { ALL_AUTOMOTIVE_CATEGORIES } from '@/lib/all-categories'

import { useRouter } from 'next/navigation'
const VoiceSearchModal = dynamic(() => import('@/components/VoiceSearchModal').then(m => m.VoiceSearchModal), { ssr: false })
const ProductDetailModal = dynamic(() => import('@/components/ProductDetailModal').then(m => m.ProductDetailModal || m.default), { ssr: false })
const BarcodeScannerModal = dynamic(() => import('@/components/BarcodeScannerModal').then(m => m.BarcodeScannerModal || m.default), { ssr: false })
const MarketplaceFilterDrawer = dynamic(() => import('@/components/MarketplaceFilterDrawer').then(m => m.MarketplaceFilterDrawer), { ssr: false })
const RequestPartModal = dynamic(() => import('@/components/RequestPartModal').then(m => m.RequestPartModal), { ssr: false })
const ShopPageReplica = dynamic(() => import('@/components/shop/ShopPageReplica'), { ssr: false })
const PhysicalDepotNetwork = dynamic(() => import('@/components/depot-network/PhysicalDepotNetwork'), { ssr: false })
const OpenStreetViewer = dynamic(() => import('@/components/depot-network/OpenStreetViewer'), { ssr: false })
import { COMMERCIAL_HUBS } from '@/lib/commercial-hubs'
import {
  Attachment,
  AttachmentMedia,
  AttachmentContent,
  AttachmentTitle,
  AttachmentDescription,
  AttachmentActions,
  AttachmentAction
} from '@/components/ui/attachment'
import { WEST_AFRICAN_COUNTRIES, getCountryByCode, getCurrencyByCode } from '@/lib/countries'
import { ScrollToTopButton } from '@/components/ScrollToTopButton'
import { VinFitmentCarousel } from '@/components/VinFitmentCarousel'
import CategoryCarouselRail from '@/components/CategoryCarouselRail'
import MarketplaceFooter from '@/components/MarketplaceFooter'

// Types
export interface Product {
  id: string
  name: string
  title?: string
  sku: string
  category: string
  subcategory?: string
  description: string
  price: number
  priceFormatted: string
  currency?: string
  country?: string
  stock: number
  unit?: string
  unitType?: string
  condition: string
  imageUrl: string
  imageUrls?: string[]
  oemNumber: string
  seller_id?: string
  sellerId?: string
  org_id?: string
  orgId?: string
  make?: string
  model?: string
  year?: string | number
  seller: {
    id?: string
    name: string
    storeName?: string
    location: string
    shopLocation?: string
    address?: string
    phone: string
    isVerified: boolean
    rating?: number
    reviewsCount?: number
    country?: string
  }
  whatsappUrl: string
  specifications?: Record<string, string>
}

interface GarageVehicle {
  id: string
  make: string
  model: string
  year: number
  trim?: string
  engine?: string
  vin?: string
  isPrimary?: boolean
}

interface ChatMessage {
  id: string
  sender: 'user' | 'assistant'
  content: string
  timestamp: Date
  imageUrl?: string
  products?: Product[]
  decodedVehicle?: any
}

interface ChatAttachment {
  file: File
  previewUrl: string
  base64: string
  name: string
  sizeKb: number
}

const CATEGORIES = [
  'All Parts',
  ...MMYTE_CATEGORIES.map((c) => c.name)
]

function sanitizePlainEnglish(text: string): string {
  if (!text) return ''
  return text.replace(/\*\*/g, '').replace(/\*/g, '').trim()
}

export type TabType = 'home' | 'shop' | 'axon' | 'garage' | 'hubs' | 'orders' | 'profile'

export default function Parts4DriveMarketplace() {
  // Theme State
  const [theme, setTheme] = useState<'light' | 'dark'>('light')

  useEffect(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('p4d_theme') : null
    if (saved === 'light' || saved === 'dark') {
      setTheme(saved)
      document.documentElement.classList.toggle('dark', saved === 'dark')
      document.documentElement.setAttribute('data-theme', saved)
    } else {
      setTheme('light')
      document.documentElement.classList.remove('dark')
      document.documentElement.setAttribute('data-theme', 'light')
    }
  }, [])

  const [canPlayDesktopVideo, setCanPlayDesktopVideo] = useState(false)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      const timer = setTimeout(() => {
        setCanPlayDesktopVideo(true)
      }, 3500)
      return () => clearTimeout(timer)
    }
  }, [])

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light'
    setTheme(next)
    if (typeof window !== 'undefined') {
      localStorage.setItem('p4d_theme', next)
      document.documentElement.classList.toggle('dark', next === 'dark')
      document.documentElement.setAttribute('data-theme', next)
    }
  }

  const router = useRouter()

  const handleOpenVendorStore = (vendor: any) => {
    if (!vendor) return
    const rawSlug = vendor.storefrontSlug || vendor.store_slug || vendor.slug || vendor.storeName || vendor.name
    const cleanSlug = rawSlug ? String(rawSlug).toLowerCase().replace(/^sel_/, '').replace(/[^a-z0-9-]/g, '').replace(/^-+|-+$/g, '') : null
    const fallbackId = vendor.id || vendor.vendorCode
    const vId = cleanSlug || fallbackId || 'store'
    if (vendor.customDomain) {
      window.location.href = `https://${vendor.customDomain}`
    } else {
      router.push(`/vendor?id=${encodeURIComponent(vId)}`)
    }
  }

  // Country / Market State (Default: 'NG', persisted in p4d_market_country cookie and localStorage)
  const [selectedCountry, setSelectedCountry] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const cookieMatch = document.cookie.match(/p4d_market_country=([a-zA-Z]{2})/i)
      if (cookieMatch) return cookieMatch[1].toUpperCase()
      const savedCountry = localStorage.getItem('p4d_market_country')
      if (savedCountry) return savedCountry.toUpperCase()
    }
    return 'NG'
  })
  const [showCountryModal, setShowCountryModal] = useState<boolean>(false)
  const [showRequestPartModal, setShowRequestPartModal] = useState<boolean>(false)
  const [heroMode, setHeroMode] = useState<'vehicle' | 'vin'>('vehicle')
  const [heroMake, setHeroMake] = useState<string>('Toyota')
  const [heroModel, setHeroModel] = useState<string>('Corolla')
  const [heroYear, setHeroYear] = useState<string>('2022')

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const cookieMatch = document.cookie.match(/p4d_market_country=([a-zA-Z]{2})/i)
      const savedCountry = localStorage.getItem('p4d_market_country') || (cookieMatch ? cookieMatch[1].toUpperCase() : null)
      if (savedCountry) {
        setSelectedCountry(savedCountry)
        setShopFilters((prev) => ({ ...prev, country: savedCountry }))
        localStorage.setItem('p4d_market_country', savedCountry)
      } else {
        // Query /api/v1/geo/me to auto-align buyer with their country
        const apiBase = process.env.NEXT_PUBLIC_P4D_URL || process.env.NEXT_PUBLIC_API_BASE_URL || ''
        fetch(`${apiBase}/api/v1/geo/me`, { credentials: 'include' })
          .then((r) => r.json())
          .then((geo) => {
            if (geo?.success && geo.country) {
              setSelectedCountry(geo.country)
              setShopFilters((prev) => ({ ...prev, country: geo.country }))
              localStorage.setItem('p4d_market_country', geo.country)
              document.cookie = `p4d_market_country=${geo.country}; Path=/; Max-Age=2592000; SameSite=Lax`
            }
          })
          .catch(() => {})
      }
    }
  }, [])

  // Automatically sync primary commercial hub when country changes
  useEffect(() => {
    const cObj = getCountryByCode(selectedCountry)
    const countryHubs = COMMERCIAL_HUBS.filter(
      (h) => h.country.toLowerCase() === (cObj?.name || '').toLowerCase()
    )
    if (countryHubs.length > 0) {
      setSelectedHubName(countryHubs[0].name)
    }
  }, [selectedCountry])

  const handleCountrySelect = (code: string) => {
    setSelectedCountry(code)
    setShopFilters((prev) => ({ ...prev, country: code }))
    if (typeof window !== 'undefined') {
      localStorage.setItem('p4d_market_country', code)
      document.cookie = `p4d_market_country=${code}; Path=/; Max-Age=2592000; SameSite=Lax`
    }
  }

  const formatPrice = (priceVal: number | string | undefined, currency?: string, country?: string) => {
    let symbol = '₦'
    const cur = (currency || '').toUpperCase()
    const ctry = (country || '').toUpperCase()

    if (cur === 'GHS' || cur === 'GH₵' || cur === 'CEDIS' || ctry === 'GH' || ctry === 'GHA' || ctry === 'GHANA') {
      symbol = 'GH₵'
    } else if (cur === 'XOF' || cur === 'CFA' || ['TG', 'TGO', 'BJ', 'BEN', 'TOGO', 'BENIN', 'CI', 'CIV', 'SN', 'SEN'].includes(ctry)) {
      symbol = 'CFA'
    } else if (cur === 'USD' || cur === '$') {
      symbol = '$'
    } else if (cur === 'EUR' || cur === '€') {
      symbol = '€'
    } else if (cur === 'GBP' || cur === '£') {
      symbol = '£'
    } else {
      const cObj = getCountryByCode(selectedCountry)
      symbol = cObj.symbol
    }

    if (priceVal === undefined || priceVal === null) return `${symbol} 0`
    const numeric = typeof priceVal === 'number' ? priceVal : parseFloat(String(priceVal).replace(/[^0-9.]/g, ''))
    return isNaN(numeric) ? `${symbol} 0` : `${symbol} ${numeric.toLocaleString()}`
  }

  // Navigation: Role-based tabs (Guest: home, shop, axon, garage, hubs | Logged-in: home, shop, axon, garage, hubs, orders)
  const [activeTab, setActiveTab] = useState<TabType>('home')
  const [selectedHubName, setSelectedHubName] = useState<string>('Ladipo Market')

  // Multi-tier Marketplace Filter State
  const [shopFilters, setShopFilters] = useState<FilterState>({
    country: selectedCountry || 'NG',
    city: '',
    hub: '',
    make: '',
    model: '',
    year: '',
    trim: '',
    category: '',
    subcategory: '',
    quality: 'all',
  })
  const [showFilterDrawer, setShowFilterDrawer] = useState(false)

  // NHTSA VIN Decoder State
  const [vinInput, setVinInput] = useState('')
  const [vinLoading, setVinLoading] = useState(false)
  const [vinDecodedVehicle, setVinDecodedVehicle] = useState<GarageVehicle | null>(null)
  const [vinError, setVinError] = useState<string | null>(null)
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false)

  // Auth State
  // Sign-in/registration itself lives in components/auth/LoginForm — the SAME
  // component /login renders. This page only owns modal visibility, notices and
  // the pending-action state that resumes after authentication.
  const [user, setUser] = useState<{ id: string; name: string; email: string; email_verified?: number | boolean; emailVerified?: boolean } | null>(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authNotice, setAuthNotice] = useState<string | null>(null)
  const [showVerifyDialog, setShowVerifyDialog] = useState(false)
  const [pendingProductId, setPendingProductId] = useState<string | null>(null)
  const [pendingAddVehicle, setPendingAddVehicle] = useState(false)
  const [showAxonModal, setShowAxonModal] = useState(false)

  // Catalog State (Zero mock data, genuine dynamic API)
  const [products, setProducts] = useState<Product[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All Parts')
  const [selectedSubcategory, setSelectedSubcategory] = useState('All')
  const [onlyCompatibleFilter, setOnlyCompatibleFilter] = useState(false)
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [catalogError, setCatalogError] = useState<string | null>(null)
  const [isRegionalFallback, setIsRegionalFallback] = useState(false)

  // User Orders State
  const [userOrders, setUserOrders] = useState<Order[]>([])
  const [loadingOrders, setLoadingOrders] = useState(false)

  // Modals State
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [isVoiceSearchOpen, setIsVoiceSearchOpen] = useState(false)

  // Virtual Garage State (Hydrated from p4d_saved_garage or initialized empty)
  const [garage, setGarage] = useState<GarageVehicle[]>([])
  const [activeVehicleId, setActiveVehicleId] = useState('')
  const [showAddCarModal, setShowAddCarModal] = useState(false)
  const [newCarMake, setNewCarMake] = useState('Toyota')
  const [newCarModel, setNewCarModel] = useState('Corolla')
  const [newCarYear, setNewCarYear] = useState('2022')
  const [newCarTrim, setNewCarTrim] = useState('LE')
  const [newCarEngine, setNewCarEngine] = useState('1.8L 4-Cylinder')
  const [newCarVin, setNewCarVin] = useState('')
  const [isDecodingModalVin, setIsDecodingModalVin] = useState(false)
  const [modalVinStatus, setModalVinStatus] = useState<string | null>(null)
  const [isCustomModel, setIsCustomModel] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const savedGarage = localStorage.getItem('p4d_saved_garage')
        if (savedGarage) {
          const parsed = JSON.parse(savedGarage)
          if (Array.isArray(parsed) && parsed.length > 0) {
            setGarage(parsed)
            setActiveVehicleId(parsed[0].id)
          }
        }
      } catch {}
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('p4d_saved_garage', JSON.stringify(garage))
      } catch {}
    }
  }, [garage])

  // Live database garage sync with Cloud Catalog
  useEffect(() => {
    const apiBase = typeof window !== 'undefined' && window.location.hostname.endsWith('parts4drive.com') ? '' : (process.env.NEXT_PUBLIC_P4D_URL || 'https://parts4drive.com')
    const userId = user?.id || (typeof localStorage !== 'undefined' ? localStorage.getItem('p4d_guest_user_id') : '')
    if (userId) {
      fetch(`${apiBase}/api/v1/garage?userId=${encodeURIComponent(userId)}`)
        .then(r => r.json())
        .then(res => {
          if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
            setGarage(res.data)
            if (!activeVehicleId) {
              setActiveVehicleId(res.data[0].id)
            }
          }
        })
        .catch(() => {})
    }
  }, [user?.id])

  // Screen 3 Interactive Chat & Product Grid State
  const [selectedVariation, setSelectedVariation] = useState<'V1' | 'V2' | 'V3' | 'V4'>('V1')
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [chatAttachment, setChatAttachment] = useState<ChatAttachment | null>(null)

  const headerFileInputRef = useRef<HTMLInputElement>(null)
  const chatFileInputRef = useRef<HTMLInputElement>(null)
  const chatEndRef = useRef<HTMLDivElement>(null)

  // Initialize Better Auth Session
  useEffect(() => {
    let isMounted = true
    const initSession = async () => {
      try {
        if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search)
          const tokenFromUrl = urlParams.get('token')
          if (tokenFromUrl) {
            authClient.storeToken(tokenFromUrl)
            const cleanUrl = window.location.pathname
            window.history.replaceState({}, document.title, cleanUrl)
          }
        }
        const session = await authClient.getSession()
        const u = session?.data?.user || session?.data
        if (isMounted && u && (u.email || u.id || u.userId)) {
          const isVerified = u.email_verified === 1 || u.email_verified === true || u.emailVerified === true
          setUser({
            id: u.id || u.userId || 'user',
            name: u.name || u.email?.split('@')[0] || 'Auto Enthusiast',
            email: u.email || '',
            email_verified: isVerified ? 1 : 0,
            emailVerified: isVerified,
          })
        }
      } catch (err) {
        console.warn('Auth session check skipped:', err)
      }
    }
    initSession()

    const unsub = subscribeToAuth((u) => {
      if (isMounted && u && (u.email || u.id || u.userId)) {
        const isVerified = u.email_verified === 1 || u.email_verified === true || u.emailVerified === true
        setUser({
          id: u.id || u.userId || 'user',
          name: u.name || u.email?.split('@')[0] || 'Auto Enthusiast',
          email: u.email || '',
          email_verified: isVerified ? 1 : 0,
          emailVerified: isVerified,
        })
      }
    })

    return () => {
      isMounted = false
      unsub()
    }
  }, [])

  // Handle NHTSA VIN Decoder
  const handleDecodeVin = async (vinToDecode?: string) => {
    const targetVin = (vinToDecode || vinInput).trim().toUpperCase()
    if (!targetVin || targetVin.length < 8) {
      setVinError('Please enter a valid VIN (minimum 8 characters).')
      return
    }
    setVinLoading(true)
    setVinError(null)
    try {
      const apiBase = process.env.NEXT_PUBLIC_P4D_URL || process.env.NEXT_PUBLIC_API_BASE_URL || 'https://parts4drive.com'
      const res = await fetch(`${apiBase}/api/v1/vehicles/decode-vin/${encodeURIComponent(targetVin)}`)
      const json = await res.json()
      if (json.success && json.make) {
        const decoded: GarageVehicle = {
          id: `vin_${Date.now()}`,
          make: json.make,
          model: json.model || 'Standard',
          year: Number(json.year) || new Date().getFullYear(),
          trim: json.trim || json.bodyClass || 'Standard',
          engine: json.engineCylinders ? `${json.engineCylinders}-Cylinder ${json.fuelType || ''}`.trim() : (json.fuelType || 'Engine'),
          vin: targetVin,
          isPrimary: true
        }
        setVinDecodedVehicle(decoded)
        setActiveVehicleId(decoded.id)
        setGarage((prev) => [decoded, ...prev.filter((v) => v.vin !== targetVin)])
        if (typeof window !== 'undefined') {
          localStorage.setItem('p4d_active_vehicle', JSON.stringify(decoded))
        }

        // Persist to D1 buyer_vehicles and log activity
        try {
          fetch(`${apiBase}/api/v1/garage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: user?.id || 'guest_buyer',
              make: decoded.make,
              model: decoded.model,
              year: decoded.year,
              trim: decoded.trim,
              engine: decoded.engine,
              vin: decoded.vin,
              isPrimary: 1
            })
          }).catch(() => {})

          fetch(`${apiBase}/api/v1/marketplace/activity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: user?.id || null,
              activityType: 'vin_decode',
              title: 'Chassis VIN Decoded',
              details: `${decoded.year} ${decoded.make} ${decoded.model} (${targetVin})`,
              metadata: decoded
            })
          }).catch(() => {})
        } catch {}
      } else {
        setVinError(json.error || 'Could not decode vehicle. Please check the VIN and retry.')
      }
    } catch (err: any) {
      setVinError(err.message || 'Failed to decode VIN.')
    } finally {
      setVinLoading(false)
    }
  }

  // Handle VIN Auto-mapping inside Add Vehicle Modal
  const handleDecodeModalVin = async (vinToDecode?: string) => {
    const targetVin = (vinToDecode || newCarVin).trim().toUpperCase()
    if (!targetVin || targetVin.length < 11) {
      setModalVinStatus('Please enter a valid 11-17 character VIN.')
      return
    }
    setIsDecodingModalVin(true)
    setModalVinStatus(null)
    try {
      const apiBase = process.env.NEXT_PUBLIC_P4D_URL || process.env.NEXT_PUBLIC_API_BASE_URL || 'https://parts4drive.com'
      const res = await fetch(`${apiBase}/api/v1/vehicles/decode-vin/${encodeURIComponent(targetVin)}`)
      const json = await res.json()
      if (json.success && json.make) {
        setNewCarMake(json.make)
        setNewCarModel(json.model || 'Standard')
        setIsCustomModel(false)
        if (json.year) setNewCarYear(String(json.year))
        if (json.trim || json.bodyClass) setNewCarTrim(json.trim || json.bodyClass)
        const engineSpec = json.engineCylinders ? `${json.engineCylinders}-Cylinder ${json.fuelType || ''}`.trim() : (json.fuelType || (json.displacementL ? `${json.displacementL}L Engine` : ''))
        if (engineSpec) setNewCarEngine(engineSpec)
        setModalVinStatus(`✓ Vehicle Matched: ${json.year || ''} ${json.make} ${json.model}`)
      } else {
        setModalVinStatus(json.error || 'Could not auto-map VIN from vehicle registry.')
      }
    } catch {
      setModalVinStatus('Failed to connect to vehicle decoding registry.')
    } finally {
      setIsDecodingModalVin(false)
    }
  }

  // Load User Orders if logged in
  useEffect(() => {
    if (!user) {
      setUserOrders([])
      return
    }
    const fetchOrders = async () => {
      try {
        setLoadingOrders(true)
        const res = await getUserOrders(user.id)
        if (res?.orders && Array.isArray(res.orders)) {
          setUserOrders(res.orders)
        }
      } catch (err) {
        console.warn('Orders fetch error:', err)
      } finally {
        setLoadingOrders(false)
      }
    }
    fetchOrders()
  }, [user])

  // Load products from Real-time API
  const fetchMarketplaceProducts = async () => {
    try {
      setLoadingProducts(true)
      setCatalogError(null)
      const params = new URLSearchParams()
      if (searchQuery.trim()) {
        params.append('keyword', searchQuery.trim())
        params.append('q', searchQuery.trim())
      }
      if (selectedCategory && selectedCategory !== 'All Parts') {
        const catObj = MMYTE_CATEGORIES.find((c) => c.name.toLowerCase() === selectedCategory.toLowerCase())
        params.append('category', (catObj?.id || selectedCategory).toLowerCase())
      }
      if (selectedSubcategory && selectedSubcategory !== 'All') {
        params.append('keyword', selectedSubcategory.toLowerCase())
      }
      if (shopFilters.hub && shopFilters.hub !== 'All Hubs' && shopFilters.hub !== 'all') {
        params.append('hub', shopFilters.hub)
      }
      params.append('country', selectedCountry)

      const apiBase = process.env.NEXT_PUBLIC_P4D_URL || process.env.NEXT_PUBLIC_API_BASE_URL || 'https://parts4drive.com'
      const res = await fetch(`${apiBase}/api/v1/marketplace/products?${params.toString()}`, {
        credentials: 'include'
      })
      const json = await res.json()

      if (json?.success && Array.isArray(json.data) && json.data.length > 0) {
        setProducts(json.data)
        setIsRegionalFallback(Boolean(json.is_regional_fallback))
      } else {
        setProducts([])
        setIsRegionalFallback(false)
      }
    } catch (err: any) {
      console.warn('Live catalog fetch error:', err)
      setCatalogError('Unable to load verified catalog. Please try again.')
    } finally {
      setLoadingProducts(false)
    }
  }

  // Verified Vendors State (Zero mock, fetched from live database)
  const [vendors, setVendors] = useState<any[]>([])
  const [loadingVendors, setLoadingVendors] = useState(false)

  // Fetch Verified Vendors from live API
  const fetchMarketplaceVendors = async () => {
    try {
      setLoadingVendors(true)
      const apiBase = process.env.NEXT_PUBLIC_P4D_URL || process.env.NEXT_PUBLIC_API_BASE_URL || 'https://parts4drive.com'
      const res = await fetch(`${apiBase}/api/v1/organizations?country=${selectedCountry}`)
      const json = await res.json()
      if (json?.success && Array.isArray(json.data)) {
        setVendors(json.data)
      } else {
        setVendors([])
      }
    } catch {
      setVendors([])
    } finally {
      setLoadingVendors(false)
    }
  }

  const isInitialProductsMount = useRef(true)
  useEffect(() => {
    fetchMarketplaceProducts()
  }, [])

  useEffect(() => {
    if (isInitialProductsMount.current) {
      isInitialProductsMount.current = false
      return
    }
    const timer = setTimeout(fetchMarketplaceProducts, 300)
    return () => clearTimeout(timer)
  }, [searchQuery, selectedCategory, selectedSubcategory, selectedCountry, shopFilters.hub])

  useEffect(() => {
    fetchMarketplaceVendors()
  }, [selectedCountry])

  // Scroll to top on mount and whenever activeTab changes
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    } catch {
      window.scrollTo(0, 0)
    }
  }, [activeTab])

  // Hydrate search query, vendor storefront, and tab from URL parameters (e.g. /shop?q=... or ?vendor=... or ?tab=shop&q=...)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const params = new URLSearchParams(window.location.search)
        const q = params.get('q') || params.get('keyword')
        const tab = params.get('tab')
        const vendorParam = params.get('vendor')

        if (vendorParam) {
          router.push(`/vendor?id=${encodeURIComponent(vendorParam)}`);
        }

        const categoryParam = params.get('category')
        const subcategoryParam = params.get('subcategory')
        const countryParam = params.get('country')

        if (countryParam) {
          const upper = countryParam.toUpperCase()
          setSelectedCountry(upper)
          setShopFilters(prev => ({ ...prev, country: upper }))
        }
        if (tab === 'shop' || categoryParam || subcategoryParam || q) {
          const targetCountry = (countryParam || selectedCountry || 'ng').toLowerCase()
          const targetParams = new URLSearchParams()
          if (q) targetParams.set('q', q)
          if (categoryParam) targetParams.set('category', categoryParam)
          if (subcategoryParam) targetParams.set('subcategory', subcategoryParam)
          const qs = targetParams.toString()
          router.replace(`/${targetCountry}/shop${qs ? '?' + qs : ''}`)
          return
        } else if (tab === 'hubs') {
          const targetCountry = (countryParam || selectedCountry || 'ng').toLowerCase()
          router.replace(`/${targetCountry}/hubs`)
          return
        } else if (tab === 'axon') {
          setShowAxonModal(true)
        } else if (tab && ['home', 'garage', 'orders', 'profile'].includes(tab)) {
          setActiveTab(tab as TabType)
        }
      } catch (err) {
        console.warn('URL params hydration error:', err)
      }
    }
  }, [])

  // Active Vehicle
  const activeVehicle = garage.find((v) => v.id === activeVehicleId) || garage[0] || null

  // Verified Fitment Products - strictly derived from real database products
  const verifiedFitmentProducts = useMemo(() => {
    if (!products || products.length === 0) return []
    if (activeVehicle?.make) {
      const targetMake = activeVehicle.make.toLowerCase()
      const matched = products.filter(p => 
        (p.make && p.make.toLowerCase() === targetMake) ||
        (p.name && p.name.toLowerCase().includes(targetMake)) ||
        (p.title && p.title.toLowerCase().includes(targetMake))
      )
      if (matched.length > 0) return matched.slice(0, 5)
    }
    return products.slice(0, 5)
  }, [products, activeVehicle])

  const guestTabs = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'shop', label: 'Shop', icon: ShoppingBag },
    { id: 'axon', label: 'Axon', icon: MessageCircle },
    { id: 'garage', label: 'Garage', icon: Car },
  ] as const

  const loggedInTabs = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'shop', label: 'Shop', icon: ShoppingBag },
    { id: 'axon', label: 'Axon', icon: MessageCircle },
    { id: 'garage', label: 'Garage', icon: Car },
    { id: 'orders', label: 'Orders', icon: Package },
  ] as const

  const currentTabs = user ? loggedInTabs : guestTabs

  const availableModelsForMake =
    GLOBAL_MAKES.find((m) => m.name.toLowerCase() === newCarMake.toLowerCase())?.models || [
      'Corolla',
      'Camry',
      'Hilux',
      'RAV4'
    ]

  const availableTrimsForMake =
    (MAKE_TRIMS as Record<string, string[]>)[newCarMake] ||
    (MAKE_TRIMS as Record<string, string[]>)[newCarMake.replace(/-/g, ' ')] ||
    DEFAULT_TRIMS

  // Filtered Products
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (onlyCompatibleFilter && activeVehicle) {
        const makeStr = activeVehicle.make.toLowerCase()
        const modelStr = (activeVehicle.model || '').toLowerCase()
        const matchesMake =
          !p.make ||
          p.make.toLowerCase() === makeStr ||
          p.name?.toLowerCase().includes(makeStr) ||
          p.description?.toLowerCase().includes(makeStr)
        if (!matchesMake) return false

        if (modelStr && modelStr !== 'standard') {
          const matchesModel =
            !p.model ||
            p.model.toLowerCase().includes(modelStr) ||
            p.name?.toLowerCase().includes(modelStr) ||
            p.description?.toLowerCase().includes(modelStr)
          if (!matchesModel) return false
        }
      }
      return true
    })
  }, [products, onlyCompatibleFilter, activeVehicle])

  // Direct WhatsApp Handoff Message Generator
  const getWhatsAppHandoffUrl = (product: Product, vehicle: GarageVehicle | null) => {
    const rawPhone = product.seller?.phone || '+2348000000000'
    const phone = rawPhone.replace(/[^0-9]/g, '')
    const vehicleStr = vehicle
      ? `${vehicle.year} ${vehicle.make} ${vehicle.model} (${vehicle.trim || 'Standard'} • ${vehicle.engine || 'Engine'})`
      : 'Unspecified vehicle'
    const verifiedSku = resolveProductSku(product)
    const text = `Hello ${product.seller?.name || 'Seller'},\n\nI found this verified part on Parts4Drive Marketplace:\n• Part: ${product.name}\n• SKU: ${verifiedSku}\n• Price: ${product.priceFormatted}\n• OEM #: ${product.oemNumber || 'Standard'}\n• My Vehicle: ${vehicleStr}\n\nPlease confirm stock availability and same-day dispatch terms.`
    return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
  }

  // Handle Product Card Click
  const handleProductClick = (product: Product) => {
    setSelectedProduct(product)
  }

  // Opens the shared VerifyEmailDialog for an already-signed-in, unverified user.
  // The dialog only knows how to RESEND a code, so kick one off here.
  const openEmailVerification = (email: string) => {
    if (!email) return
    setShowVerifyDialog(true)
    authClient.requestOtp(email, 'email_verification').catch(() => {})
  }

  // Shared post-auth callback handed to <LoginForm onAuthed={...} />
  const handleAuthed = (userData: any, _token?: string) => {
    const isVerified =
      userData?.email_verified === 1 ||
      userData?.email_verified === true ||
      userData?.emailVerified === true

    setUser({
      id: userData?.id || userData?.userId || 'user',
      name: userData?.name || userData?.email?.split('@')[0] || 'Auto Enthusiast',
      email: userData?.email || '',
      email_verified: isVerified ? 1 : 0,
      emailVerified: isVerified,
    })
    setShowAuthModal(false)
    setAuthNotice(null)

    if (isVerified) {
      setActiveTab('garage')
    } else {
      openEmailVerification(userData?.email)
    }

    // Resume whatever the visitor was doing before we asked them to sign in.
    if (pendingProductId) {
      const p = products.find((prod) => prod.id === pendingProductId)
      if (p) setSelectedProduct(p)
      setPendingProductId(null)
    }
    if (pendingAddVehicle) {
      setShowAddCarModal(true)
      setPendingAddVehicle(false)
    }
  }

  // Unified Role-Based Tab Switcher (Directory Routing & Gated Garage)
  const handleTabSelect = (tabId: TabType) => {
    if (tabId === 'shop') {
      router.push(`/${selectedCountry.toLowerCase()}/shop`)
      return
    }
    if (tabId === 'hubs') {
      router.push(`/${selectedCountry.toLowerCase()}/hubs`)
      return
    }
    if (tabId === 'axon') {
      setShowAxonModal(true)
      return
    }
    if (tabId === 'garage') {
      const isVerified = user && (user.email_verified === 1 || user.email_verified === true || user.emailVerified === true)
      if (!user) {
        setAuthNotice('Please sign in or register to access your Virtual Garage.')
        setShowAuthModal(true)
        return
      }
      if (!isVerified) {
        setAuthNotice('Please complete email verification to access your Virtual Garage.')
        openEmailVerification(user.email)
        return
      }
    }
    setActiveTab(tabId)
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href)
        url.searchParams.delete('tab')
        window.history.replaceState({}, '', url.toString())
      } catch {}
    }
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    } catch {
      window.scrollTo(0, 0)
    }
  }

  // Handle Virtual Garage Open Add Vehicle (Auth & OTP Gated)
  const handleOpenAddVehicle = () => {
    const isVerified = user && (user.email_verified === 1 || user.email_verified === true || user.emailVerified === true)
    if (!user) {
      setAuthNotice('Please sign in or register to add vehicles to your Virtual Garage.')
      setPendingAddVehicle(true)
      setShowAuthModal(true)
      return
    }
    if (!isVerified) {
      setAuthNotice('Please complete email verification before adding vehicles to your garage.')
      setPendingAddVehicle(true)
      openEmailVerification(user.email)
      return
    }
    setShowAddCarModal(true)
  }

  // Sign Out via Better Auth
  const handleSignOut = async () => {
    try {
      await authClient.signOut()
    } catch (err) {
      console.warn('Sign out error:', err)
    }
    setUser(null)
    setActiveTab('home')
  }

  // Handle Image Upload from Header
  const handleHeaderImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = async (event) => {
      const base64 = event.target?.result as string
      setChatAttachment({
        file,
        previewUrl: base64,
        base64,
        name: file.name,
        sizeKb: Math.round(file.size / 1024)
      })
      setActiveTab('axon')
    }
    reader.readAsDataURL(file)
  }

  // Handle Image Upload inside Chat Composer
  const handleChatImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (event) => {
      const base64 = event.target?.result as string
      setChatAttachment({
        file,
        previewUrl: base64,
        base64,
        name: file.name,
        sizeKb: Math.round(file.size / 1024)
      })
    }
    reader.readAsDataURL(file)
  }

  // General sendMessage supporting text input and pre-message starter chips
  const sendMessage = async (overrideText?: string) => {
    const queryText = (typeof overrideText === 'string' ? overrideText : chatInput).trim()
    const attachmentToSend = chatAttachment
    if ((!queryText && !attachmentToSend) || chatLoading) return

    setChatInput('')
    setChatAttachment(null)
    if (chatFileInputRef.current) chatFileInputRef.current.value = ''

    const userMsgId = Date.now().toString()
    setChatMessages((prev) => [
      ...prev,
      {
        id: userMsgId,
        sender: 'user',
        content: queryText || (attachmentToSend ? 'Uploaded component photo for visual part identification' : ''),
        timestamp: new Date(),
        imageUrl: attachmentToSend ? attachmentToSend.previewUrl : undefined
      }
    ])

    setChatLoading(true)
    const apiBase = process.env.NEXT_PUBLIC_P4D_URL || process.env.NEXT_PUBLIC_API_BASE_URL || 'https://parts4drive.com'

    try {
      let aiText = ''
      let returnedVehicle: any = null
      let returnedProducts: Product[] = []

      try {
        const aiRes = await fetch(`${apiBase}/api/v1/ai/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: queryText,
            activeVehicle: activeVehicle,
            buyerMode: true,
            orgId: 'marketplace',
            country: selectedCountry,
            attachment: attachmentToSend ? attachmentToSend.base64 : undefined,
          }),
        })
        const aiJson = await aiRes.json()
        aiText = aiJson?.response || aiJson?.text || aiJson?.content || ''
        if (aiJson?.decodedVehicle) {
          returnedVehicle = aiJson.decodedVehicle
          const newVehId = `veh_${Date.now()}`
          const newVeh: GarageVehicle = {
            id: newVehId,
            make: aiJson.decodedVehicle.make,
            model: aiJson.decodedVehicle.model,
            year: aiJson.decodedVehicle.year,
            trim: aiJson.decodedVehicle.trim || aiJson.decodedVehicle.bodyClass || 'Standard',
            vin: aiJson.decodedVehicle.vin,
            isPrimary: true
          }
          setGarage((prev) => [newVeh, ...prev.filter(v => v.vin !== newVeh.vin)])
          setActiveVehicleId(newVehId)
        }
        if (Array.isArray(aiJson?.matchingProducts) && aiJson.matchingProducts.length > 0) {
          returnedProducts = aiJson.matchingProducts
        } else if (Array.isArray(aiJson?.products) && aiJson.products.length > 0) {
          returnedProducts = aiJson.products
        }
      } catch (aiErr) {
        console.warn('Axon AI endpoint error:', aiErr)
      }

      let foundProducts: Product[] = returnedProducts

      if (foundProducts.length === 0) {
        const targetVehicle = returnedVehicle || activeVehicle
        const params = new URLSearchParams()
        if (targetVehicle) {
          params.append('make', (targetVehicle.make || '').toLowerCase())
          params.append('model', (targetVehicle.model || '').toLowerCase())
          params.append('limit', '8')
        } else {
          params.append('keyword', queryText || 'brakes')
          params.append('q', queryText || 'brakes')
          params.append('limit', '8')
        }
        params.append('country', selectedCountry)

        const res = await fetch(`${apiBase}/api/v1/marketplace/products?${params.toString()}`, {
          credentials: 'include'
        })
        const json = await res.json()
        foundProducts = json?.success && Array.isArray(json.data) ? json.data : []
      }

      const noMatchNotice = `No verified components currently match "${queryText || (activeVehicle ? `${activeVehicle.year} ${activeVehicle.make} ${activeVehicle.model}` : 'your search')}" in the active database. As our verified vendor depots synchronize their inventory, matching parts will appear here. If you need this part urgently, please reach out to our team for custom dispatch sourcing.`

      setChatMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          sender: 'assistant',
          content: sanitizePlainEnglish(
            aiText || (foundProducts.length > 0
              ? `Found ${foundProducts.length} verified listings for "${queryText || (activeVehicle ? `${activeVehicle.year} ${activeVehicle.make} ${activeVehicle.model}` : 'your vehicle')}". All parts verified with exact vehicle fitment.`
              : noMatchNotice
            )
          ),
          timestamp: new Date(),
          products: foundProducts,
          decodedVehicle: returnedVehicle
        }
      ])
    } catch {
      setChatMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          sender: 'assistant',
          content: 'Unable to query the parts catalog at this moment. Please check your network connection and try again.',
          timestamp: new Date(),
          products: []
        }
      ])
    } finally {
      setChatLoading(false)
    }
  }

  // Handle Copilot Chat Send
  const handleSendMessage = async (e?: React.FormEvent | string) => {
    if (e && typeof e !== 'string' && 'preventDefault' in e) {
      e.preventDefault()
    }
    const text = typeof e === 'string' ? e : undefined
    sendMessage(text)
  }

  // Handle Voice Search Submission with live database query (Zero mock fallback)
  const handleVoiceSearchResult = async (query: string) => {
    setIsVoiceSearchOpen(false)
    setSearchQuery(query)
    setActiveTab('shop')

    const apiBase = process.env.NEXT_PUBLIC_P4D_URL || process.env.NEXT_PUBLIC_API_BASE_URL || 'https://parts4drive.com'
    try {
      const params = new URLSearchParams()
      params.append('keyword', query.trim())
      params.append('q', query.trim())
      params.append('country', selectedCountry)
      if (activeVehicle) {
        params.append('make', activeVehicle.make.toLowerCase())
      }
      const res = await fetch(`${apiBase}/api/v1/marketplace/products?${params.toString()}`, { credentials: 'include' })
      const json = await res.json()
      const found: Product[] = (json?.success && Array.isArray(json.data)) ? json.data : []
      setProducts(found)
      setChatMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          sender: 'user',
          content: query,
          timestamp: new Date()
        },
        {
          id: (Date.now() + 1).toString(),
          sender: 'assistant',
          content: found.length > 0
            ? `Searching live verified inventory for: "${query}". Found ${found.length} verified parts with 100% exact fit guarantees.`
            : `No verified parts found matching "${query}" in the active market catalog.`,
          timestamp: new Date(),
          products: found
        }
      ])
    } catch {
      setChatMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          sender: 'user',
          content: query,
          timestamp: new Date()
        },
        {
          id: (Date.now() + 1).toString(),
          sender: 'assistant',
          content: `Unable to search live catalog for "${query}". Please verify connection and retry.`,
          timestamp: new Date(),
          products: []
        }
      ])
    }
  }

  // Handle Add Vehicle in Virtual Garage
  const handleAddVehicle = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) {
      setAuthNotice('Please sign in or register to add vehicles to your Virtual Garage.')
      setShowAuthModal(true)
      return
    }
    if (!newCarMake || !newCarModel) return

    const tempId = `gar_${Date.now()}`
    const newCar: GarageVehicle = {
      id: tempId,
      make: newCarMake,
      model: newCarModel,
      year: Number(newCarYear),
      trim: newCarTrim,
      engine: newCarEngine,
      vin: newCarVin || undefined,
      isPrimary: garage.length === 0
    }

    setGarage((prev) => [newCar, ...prev])
    setActiveVehicleId(newCar.id)
    setShowAddCarModal(false)
    setNewCarMake('Toyota')
    setNewCarModel('Corolla')
    setNewCarVin('')
    setModalVinStatus(null)
    setIsCustomModel(false)

    try {
      const apiBase = typeof window !== 'undefined' && window.location.hostname.endsWith('parts4drive.com') ? '' : (process.env.NEXT_PUBLIC_P4D_URL || 'https://parts4drive.com')
      const res = await fetch(`${apiBase}/api/v1/garage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          make: newCar.make,
          model: newCar.model,
          year: newCar.year,
          trim: newCar.trim,
          engine: newCar.engine,
          vin: newCar.vin,
          isPrimary: newCar.isPrimary ? 1 : 0
        })
      })
      const data = await res.json()
      if (data?.success && data?.data?.id) {
        setGarage((prev) => prev.map(v => v.id === tempId ? { ...v, id: data.data.id } : v))
        if (activeVehicleId === tempId) {
          setActiveVehicleId(data.data.id)
        }
      }
    } catch (err) {
      console.warn('Could not persist vehicle to cloud garage:', err)
    }
  }

  return (
    <div
      className={`min-h-screen transition-colors duration-200 flex flex-col font-ui overflow-x-hidden min-w-0 max-w-full ${
        theme === 'dark' ? 'bg-[#05070B] text-slate-100' : 'bg-[#F6F8FC] text-[#0A1B4A]'
      }`}
    >
      {/* Hidden file input for visual search */}
      <input
        type="file"
        ref={headerFileInputRef}
        onChange={handleHeaderImageUpload}
        accept="image/*"
        className="hidden"
      />
      <input
        type="file"
        ref={chatFileInputRef}
        onChange={handleChatImageUpload}
        accept="image/*"
        className="hidden"
      />

      {/* UNIFIED SLEEK TOP BAR (Matching Reference Design Image 2) */}
      <header className="sticky top-0 z-50 bg-[#060D1E] border-b border-white/10 px-3 sm:px-6 lg:px-8 py-3 transition-colors">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-4">
          {/* Brand Logo & Dropdown Navigation */}
          <div className="flex items-center gap-6 lg:gap-8 min-w-0">
            <div
              onClick={() => {
                setActiveTab('home')
                setSearchQuery('')
                setSelectedCategory('All Parts')
              }}
              className="cursor-pointer select-none shrink-0 py-1"
            >
              <BrandLogo theme="dark" priority className="h-8 sm:h-9 w-auto" />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center gap-1.5 text-sm font-medium text-slate-300">
              <Link
                href={`/${selectedCountry.toLowerCase()}/shop`}
                className="px-3 py-1.5 rounded-lg hover:text-white hover:bg-white/5 transition-colors text-xs font-semibold"
              >
                <span>Shop Catalog</span>
              </Link>
              <button
                type="button"
                onClick={() => handleTabSelect('garage')}
                className="px-3 py-1.5 rounded-lg hover:text-white hover:bg-white/5 transition-colors flex items-center gap-1 text-xs font-semibold cursor-pointer"
              >
                <span>By Vehicle</span>
                <ChevronDown className="size-3.5 opacity-60" />
              </button>
              <Link
                href={`/${selectedCountry.toLowerCase()}/hubs`}
                className="px-3 py-1.5 rounded-lg hover:text-white hover:bg-white/5 transition-colors text-xs font-semibold"
              >
                <span>Hubs</span>
              </Link>
              <Link
                href={`/${selectedCountry.toLowerCase()}/sellers`}
                className="px-3 py-1.5 rounded-lg hover:text-white hover:bg-white/5 transition-colors text-xs font-semibold"
              >
                <span>Verified Sellers</span>
              </Link>
              <button
                type="button"
                onClick={() => setShowAxonModal(true)}
                className="px-3 py-1.5 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 transition-colors flex items-center gap-1.5 text-xs font-bold cursor-pointer shadow-xs ml-1"
              >
                <Wrench className="size-3.5 text-blue-400" />
                <span>Axon Concierge</span>
              </button>
              <Link
                href="/signup?role=merchant"
                className="px-3 py-1.5 rounded-lg text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors ml-1 text-xs font-semibold"
              >
                Sell on Parts4Drive
              </Link>
            </nav>
          </div>

          {/* Right Action Controls */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Search Icon Trigger */}
            <button
              type="button"
              onClick={() => {
                if (activeTab !== 'home') {
                  setActiveTab('home')
                  setTimeout(() => {
                    document.getElementById('hero-search-input')?.focus()
                  }, 100)
                } else {
                  document.getElementById('hero-search-input')?.focus()
                }
              }}
              className="p-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-full transition-colors"
              title="Search automotive parts"
              aria-label="Search"
            >
              <Search className="size-4" />
            </button>

            {/* My Garage shortcut button */}
            <button
              type="button"
              onClick={() => handleTabSelect('garage')}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-slate-200 text-xs font-semibold transition-colors"
            >
              <Car className="size-3.5 text-blue-400" />
              <span>My Garage</span>
              {activeVehicle && (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              )}
            </button>

            {/* Notifications Bell */}
            <button
              type="button"
              onClick={() => setActiveTab('orders')}
              className="p-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-full transition-colors relative"
              title="Notifications and Orders"
              aria-label="Notifications"
            >
              <Bell className="size-4" />
              <span className="absolute top-1.5 right-1.5 size-2 bg-blue-500 rounded-full" />
            </button>

            {/* Country Selector Header Button */}
            <button
              type="button"
              onClick={() => setShowCountryModal(true)}
              aria-label="Select Regional Market Country"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-xs font-bold text-white transition-colors"
              title="Select Regional Market Country"
            >
              <span className="text-sm leading-none">{getCountryByCode(selectedCountry).flag}</span>
              <span className="font-mono text-[11px] font-bold">{selectedCountry}</span>
            </button>

            {/* User Profile Pill / Auth */}
            {user ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setActiveTab('profile')}
                  aria-label="User profile and account settings"
                  className="flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-white text-xs font-semibold transition-colors"
                >
                  <div className="size-7 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xs">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline truncate max-w-[100px]">{user.name}</span>
                  <ChevronDown className="size-3 opacity-60" />
                </button>
                <button
                  type="button"
                  onClick={handleSignOut}
                  title="Sign Out"
                  aria-label="Sign Out"
                  className="size-8 rounded-full border border-white/10 bg-white/5 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 flex items-center justify-center text-slate-400 transition-colors"
                >
                  <LogOut className="size-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowAuthModal(true)}
                aria-label="Sign In or Register"
                className="flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-white text-xs font-semibold transition-colors"
              >
                <div className="size-7 rounded-full bg-slate-700 text-white flex items-center justify-center">
                  <User className="size-3.5" />
                </div>
                <span className="hidden sm:inline">John Doe</span>
                <ChevronDown className="size-3 opacity-60" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* MAIN CONTENT AREA */}
      {/* MAIN CONTENT AREA */}
      <main className={`flex-1 w-full min-w-0 ${activeTab === 'home' ? 'p-0 pb-0' : 'max-w-[1560px] w-full mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-5 pb-28'}`}>

        {/* ========================================================================= */}
        {/* ========================================================================= */}
        {/* HOMEPAGE / MARKETPLACE LANDING (EXACT REPLICA OF REFERENCE DESIGN) */}
        {/* ========================================================================= */}
        {activeTab === 'home' && (
          <div className="w-full flex flex-col">
            {/* 1. CINEMATIC HERO SECTION (Image 2 Exact Layout) */}
            <section className="relative min-h-[520px] lg:min-h-[580px] bg-[#060D1E] overflow-hidden text-white flex items-center">
              {/* Native background hero image + optional deferred desktop video */}
              <div className="absolute right-0 top-0 bottom-0 w-full lg:w-[60%] pointer-events-none overflow-hidden">
                <img
                  src="/images/parts4drive-hero-poster.webp"
                  alt="Parts4Drive Genuine Auto Parts"
                  loading="eager"
                  decoding="async"
                  width={1280}
                  height={720}
                  className="w-full h-full object-cover object-[center_right]"
                />
                {canPlayDesktopVideo && (
                  <video
                    className="absolute inset-0 w-full h-full object-cover object-[center_right] hidden lg:block"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                    aria-hidden="true"
                  >
                    <source src="/videos/parts4drive-hero.mp4" type="video/mp4" />
                  </video>
                )}
                {/* Soft gradient blend into the dark navy background on the left */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#060D1E] via-[#060D1E]/85 to-transparent lg:via-[#060D1E]/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060D1E] via-transparent to-transparent opacity-90" />
              </div>

              {/* Hero Marketplace Content */}
              <div className="relative z-10 max-w-[1500px] w-full mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-16">
                <div className="max-w-2xl">
                  {/* Eyebrow */}
                  <div className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/10 px-3.5 py-1 text-xs font-semibold text-blue-300 mb-3 shadow-xs">
                    <span className="relative flex h-2 w-2">
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                    </span>
                    <span className="tracking-wider uppercase text-[11px]">
                      AFRICA&apos;S VERIFIED AUTOMOTIVE MARKETPLACE
                    </span>
                  </div>

                  {/* Headline */}
                  <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-black tracking-tight text-white leading-[1.05]">
                    The right part for<br />
                    your <span className="text-[#38BDF8]">exact car.</span>
                  </h1>

                  {/* Subtitle */}
                  <p className="mt-4 text-sm sm:text-base text-slate-300 max-w-xl leading-relaxed">
                    Verified automotive parts, matched to your vehicle using VIN, fitment data and AI — from trusted sellers across Africa.
                  </p>

                  {/* Search Input Bar Capsule */}
                  <div className="mt-7 max-w-xl">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault()
                        handleTabSelect('shop')
                      }}
                      className="relative flex items-center bg-white rounded-full shadow-xl p-1.5 border border-white/20"
                    >
                      <Search className="size-5 text-slate-400 ml-3 shrink-0" />
                      <input
                        id="hero-search-input"
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search a part, OEM number, or describe what you need..."
                        className="w-full pl-3 pr-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none bg-transparent"
                      />
                      <button
                        type="submit"
                        className="size-10 rounded-full bg-[#0A1B4A] hover:bg-[#0f2560] text-white flex items-center justify-center shrink-0 shadow-md transition-all active:scale-95"
                        title="Search"
                      >
                        <Search className="size-4" />
                      </button>
                    </form>

                    {/* 3 Switcher Buttons */}
                    <div className="flex flex-wrap items-center gap-2.5 mt-3.5">
                      <button
                        type="button"
                        onClick={() => handleTabSelect('garage')}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
                      >
                        <Car className="size-3.5 text-blue-400" />
                        <span>Select Vehicle</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBarcodeScanner(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
                      >
                        <ScanLine className="size-3.5 text-blue-400" />
                        <span>Decode VIN</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTabSelect('axon')}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
                      >
                        <MessageCircle className="size-3.5 text-blue-400" />
                        <span>Ask Axon</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* The original live vehicle/VIN result carousel remains available immediately below the cinematic hero. */}
            {vinDecodedVehicle && (
              <div className="px-3 sm:px-6 max-w-[1500px] mx-auto w-full">
                <VinFitmentCarousel
                  make={vinDecodedVehicle.make}
                  model={vinDecodedVehicle.model}
                  year={vinDecodedVehicle.year}
                  onProductClick={(p) => handleProductClick(p as any)}
                  onShopAll={() => handleTabSelect('shop')}
                />
              </div>
            )}

            {/* ========================================================================= */}
            {/* HOMEPAGE MAIN CONTENT CONTAINER (P4D High-Contrast Clean Layout) */}
            {/* ========================================================================= */}
            <div className="bg-[#F4F7FB] dark:bg-[#070D1E] w-full py-8 sm:py-10 space-y-8 sm:space-y-12">
              {/* ========================================================================= */}
              {/* 1. YOUR GARAGE QUICK-ACCESS BAR */}
              {/* ========================================================================= */}
              <section className="px-3 sm:px-6 max-w-[1500px] mx-auto w-full">
                <div className="bg-white dark:bg-[#0E1526] rounded-2xl border border-slate-200/90 dark:border-slate-800/90 p-4 sm:p-5 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 transition-all">
                  <div className="flex items-center gap-3.5 sm:gap-4 min-w-0">
                    <VehicleBrandLogo
                      brand={activeVehicle?.make || 'Toyota'}
                      size="lg"
                      variant="badge"
                      className="shadow-sm"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-blue-600 dark:text-blue-400">
                          Your Garage (Active Vehicle)
                        </span>
                        <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      </div>
                      <h2 className="font-display font-extrabold text-base sm:text-lg text-[#060D1E] dark:text-white truncate">
                        {activeVehicle ? `${activeVehicle.year} ${activeVehicle.make} ${activeVehicle.model}` : '2021 Toyota Corolla LE'}
                      </h2>
                      <p className="text-xs text-slate-600 dark:text-slate-300 truncate">
                        {activeVehicle
                          ? `${activeVehicle.engine || '2.0L 4-Cylinder'} • ${activeVehicle.trim || 'FWD • 4dr Sedan'} | VIN: ${activeVehicle.vin ? activeVehicle.vin.slice(0, 8) + '...' : 'JT2B...'} • Saved to Garage`
                          : '2.0L • FWD • 4dr Sedan | VIN: JT2B... • Saved to Account'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 shrink-0 self-start md:self-center">
                    <button
                      type="button"
                      onClick={() => setShowAddCarModal(true)}
                      className="px-4 py-2.5 rounded-full border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-[#060D1E] dark:text-slate-200 font-bold text-xs transition-colors"
                    >
                      Change Vehicle
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShopFilters(prev => ({
                          ...prev,
                          make: activeVehicle?.make || 'Toyota',
                          model: activeVehicle?.model || 'Corolla'
                        }))
                        setOnlyCompatibleFilter(true)
                        handleTabSelect('shop')
                      }}
                      className="px-5 py-2.5 rounded-full bg-[#0066FF] hover:bg-[#0052cc] text-white font-bold text-xs shadow-md shadow-blue-500/20 transition-all flex items-center gap-1.5"
                    >
                      <span>Find Parts For This Vehicle</span>
                      <ArrowRight className="size-3.5" />
                    </button>
                  </div>
                </div>
              </section>

              {/* ========================================================================= */}
              {/* 2. EXACT-FIT PARTS ROW (5 CARDS WITH 3 CONFIDENCE TIERS) */}
              {/* ========================================================================= */}
              <section className="space-y-4 px-3 sm:px-6 max-w-[1500px] mx-auto w-full">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 border-b border-slate-200/80 dark:border-slate-800/80 pb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-display font-extrabold text-xl sm:text-2xl text-[#060D1E] dark:text-white tracking-tight">
                        Exact-Fit Parts
                      </h2>
                      <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 text-[10px] font-extrabold tracking-wide uppercase flex items-center gap-1">
                        <CheckCircle2 className="size-3 text-emerald-500" />
                        100% Fitment Guarantee
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-1">
                      OEM & Tier-1 Aftermarket parts verified for your {activeVehicle ? `${activeVehicle.year} ${activeVehicle.make} ${activeVehicle.model}` : '2021 Toyota Corolla LE'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setOnlyCompatibleFilter(true)
                      handleTabSelect('shop')
                    }}
                    className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 flex items-center gap-1 shrink-0 group"
                  >
                    <span>View all 142 parts</span>
                    <ArrowRight className="size-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {loadingProducts ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="bg-white dark:bg-[#0E1526] rounded-2xl border border-slate-200/80 dark:border-slate-800/80 p-3 sm:p-3.5 space-y-3 animate-pulse h-full flex flex-col justify-between">
                        <div className="space-y-2">
                          <div className="h-4 w-20 bg-slate-200 dark:bg-slate-800 rounded-full" />
                          <div className="w-full aspect-square bg-slate-200 dark:bg-slate-800 rounded-xl" />
                          <div className="space-y-1.5 pt-1">
                            <div className="h-3 w-16 bg-slate-200 dark:bg-slate-800 rounded" />
                            <div className="h-4 w-full bg-slate-200 dark:bg-slate-800 rounded" />
                            <div className="h-3 w-24 bg-slate-200 dark:bg-slate-800 rounded" />
                          </div>
                        </div>
                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center mt-auto">
                          <div className="h-5 w-16 bg-slate-200 dark:bg-slate-800 rounded" />
                          <div className="h-7 w-20 bg-slate-200 dark:bg-slate-800 rounded-full" />
                        </div>
                      </div>
                    ))
                  ) : verifiedFitmentProducts.length === 0 ? (
                    <div className="col-span-full p-8 rounded-2xl bg-white dark:bg-[#0E1526] border border-slate-200/80 dark:border-slate-800/80 text-center space-y-3">
                      <div className="size-10 rounded-full bg-blue-50 dark:bg-blue-950/50 flex items-center justify-center mx-auto text-blue-600">
                        <ShieldCheck className="size-5" />
                      </div>
                      <p className="text-xs font-bold text-slate-800 dark:text-slate-200">
                        {activeVehicle ? `No direct parts in database for ${activeVehicle.year} ${activeVehicle.make} ${activeVehicle.model} yet.` : 'Browse verified OEM parts from live regional merchants.'}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleTabSelect('shop')}
                        className="px-4 py-2 rounded-full bg-[#0066FF] text-white text-xs font-bold hover:bg-[#0052cc] transition-colors"
                      >
                        Explore Marketplace Catalog
                      </button>
                    </div>
                  ) : (
                    verifiedFitmentProducts.map((prod) => {
                      const prodName = prod.title || prod.name
                      const prodPrice = prod.price || 0
                      const prodImg = prod.imageUrl || (prod as any).image || '/images/default-part.webp'
                      const prodOem = prod.oemNumber || prod.sku || 'OEM-SPEC'
                      const prodRating = prod.seller?.rating || (prod as any).rating || 4.9
                      const prodReviews = prod.seller?.reviewsCount || (prod as any).reviews || 1

                      return (
                        <div
                          key={prod.id}
                          onClick={() => handleProductClick(prod)}
                          className="group bg-white dark:bg-[#0A1B4A] rounded-2xl border border-slate-200/60 dark:border-slate-800 p-3 sm:p-3.5 flex flex-col justify-between shadow-vite-card hover:shadow-vite-elevated hover:border-[#0A1B4A]/30 dark:hover:border-blue-400/40 transition-all cursor-pointer relative overflow-hidden h-full"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-bold text-[#8D840A] dark:text-[#FEF08A] bg-[#FCFBEB] dark:bg-[#8D840A]/25 border border-[#EFECC8] dark:border-[#8D840A]/40 px-2 py-0.5 rounded-full whitespace-nowrap">
                              <Check className="size-2.5 stroke-[3]" />
                              ACES Exact
                            </span>
                            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono font-bold bg-slate-100 dark:bg-[#060F2A] px-2 py-0.5 rounded-full border border-slate-200/60 dark:border-slate-700">
                              {prod.seller?.country === 'GH' ? '🇬🇭 GH' : '🇳🇬 NG'}
                            </span>
                          </div>

                          {/* 1:1 SQUARE MEDIA CONTAINER */}
                          <div className="w-full aspect-square rounded-xl bg-slate-50 dark:bg-[#060F2A] flex items-center justify-center p-2 mb-2.5 group-hover:scale-105 transition-transform overflow-hidden relative">
                            <Image
                              src={prodImg}
                              alt={prodName}
                              fill
                              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 220px"
                              className="object-contain p-2"
                              unoptimized
                            />
                          </div>

                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-slate-500 dark:text-slate-400 truncate">
                              <CategoryIcon category={prod.category} size="xs" variant="raw" className="size-3.5 shrink-0 object-contain" />
                              <span className="truncate">{prod.category}</span>
                            </div>
                            <h3 className="font-display font-bold text-xs sm:text-sm text-[#0A1B4A] dark:text-white line-clamp-2 h-9 leading-snug group-hover:text-blue-600 transition-colors">
                              {prodName}
                            </h3>
                            <p className="text-[10px] font-mono text-slate-500 dark:text-slate-400 truncate">
                              Part # {prodOem}
                            </p>

                            <div className="flex items-center gap-1 pt-0.5 text-[11px]">
                              <div className="flex items-center text-amber-400">
                                <Star className="size-3 fill-amber-400" />
                              </div>
                              <span className="font-bold text-[#0A1B4A] dark:text-slate-200">
                                {prodRating}
                              </span>
                              <span className="text-slate-400 text-[10px]">
                                ({prodReviews})
                              </span>
                            </div>
                          </div>

                          <div className="mt-auto pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2 min-w-0 max-w-full">
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold block">
                                  Verified Price
                                </span>
                                <span className="font-display font-black text-sm sm:text-base text-[#0A1B4A] dark:text-white tabular-nums">
                                  {prod.priceFormatted || `${prod.currency === 'GHS' ? 'GH₵' : prod.currency === 'USD' ? '$' : '₦'}${prodPrice.toLocaleString()}`}
                                </span>
                              </div>
                              <button
                                type="button"
                                aria-label={`View parts specification for ${prodName}`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleProductClick(prod)
                                }}
                                className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-[#0D225C] dark:hover:bg-[#122C72] text-[#0A1B4A] dark:text-white transition-colors border border-slate-200 dark:border-slate-700 flex items-center justify-center shrink-0 cursor-pointer active:scale-95 shadow-2xs"
                              >
                                Details
                              </button>
                            </div>

                            <button
                              type="button"
                              aria-label={`Order ${prodName} via WhatsApp`}
                              onClick={(e) => {
                                e.stopPropagation()
                                const waUrl = getWhatsAppHandoffUrl(prod, activeVehicle)
                                if (waUrl) {
                                  window.open(waUrl, "_blank", "noopener,noreferrer")
                                } else {
                                  handleProductClick(prod)
                                }
                              }}
                              className="w-full max-w-full box-border min-h-[38px] sm:min-h-[40px] px-3 py-1.5 sm:py-2 rounded-full bg-[#0A1B4A] hover:bg-[#0f2560] dark:bg-blue-600 dark:hover:bg-blue-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-sm transition-all hover:scale-[1.01] active:scale-[0.98] cursor-pointer overflow-hidden min-w-0"
                            >
                              <MessageCircle className="size-3.5 sm:size-4 fill-white text-[#0A1B4A] dark:text-blue-600 shrink-0" />
                              <span className="font-bold text-[10.5px] sm:text-xs leading-tight text-center break-words min-w-0">
                                Order via WhatsApp
                              </span>
                            </button>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </section>

              {/* ========================================================================= */}
              {/* 3. SHOP BY CATEGORY (CIRCULAR CAROUSEL RAIL) */}
              {/* ========================================================================= */}
              <div className="px-3 sm:px-6 max-w-[1500px] mx-auto w-full">
                <CategoryCarouselRail country={selectedCountry} />
              </div>

              {/* ========================================================================= */}
              {/* 4. THREE-MODULE BENTO ROW (HUBS 50%, AXON AI 25%, SELL 25%) */}
              {/* ========================================================================= */}
              <section className="px-3 sm:px-6 max-w-[1500px] mx-auto w-full">
                <h2 className="sr-only">Automotive Hubs, Axon AI &amp; Merchant Operating System</h2>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-5">
                  {/* Module 1: Local Hubs & Markets with Street View */}
                  <PhysicalDepotNetwork
                    country={selectedCountry}
                    onSelectHub={(hubName) => {
                      setSelectedHubName(hubName)
                      setShopFilters(prev => ({ ...prev, hub: hubName }))
                      handleTabSelect('hubs')
                    }}
                    onExploreAll={() => handleTabSelect('hubs')}
                  />

                  {/* Module 2: Meet Axon AI (25% / col-span-1) */}
                  <div className="rounded-3xl bg-[#0A1B4A] border border-slate-800 p-5 sm:p-6 text-white shadow-xl flex flex-col justify-between relative overflow-hidden group">
                    <div className="relative z-10 space-y-3">
                      {/* Badge and Status */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-cyan-300 bg-cyan-950/50 border border-cyan-500/20 px-2.5 py-0.5 rounded-full">
                          <Wrench className="size-3 text-cyan-300" />
                          <span>Parts Assistant</span>
                        </div>
                        <span className="flex items-center gap-1 text-[10px] font-mono text-emerald-400 bg-emerald-950/40 px-2 py-0.5 rounded-full border border-emerald-500/20">
                          <span className="size-1.5 rounded-full bg-emerald-400 animate-ping" />
                          <span>Online</span>
                        </span>
                      </div>

                      <div>
                        <h3 className="font-display font-extrabold text-lg sm:text-xl text-white tracking-tight">
                          Meet Axon AI
                        </h3>
                        <p className="text-xs text-slate-300 leading-relaxed mt-1">
                          Diagnose symptoms, verify OEM numbers &amp; transcribe mechanics&apos; voice notes in Pidgin, Yoruba, Twi, Swahili &amp; English.
                        </p>
                      </div>

                      {/* Interactive Prompts / Voice Note Simulation */}
                      <div className="space-y-1.5 pt-1">
                        <span className="text-[10px] uppercase font-bold tracking-wider text-cyan-400/90 block">
                          Tap to Test Diagnostic Prompts:
                        </span>
                        
                        <button
                          type="button"
                          onClick={() => {
                            setChatInput("Oga my brake dey do kpa-kpa sound for front wheel");
                            handleTabSelect('axon');
                          }}
                          className="w-full text-left p-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-cyan-500/40 text-[11px] text-slate-300 transition-all flex items-center gap-2 group/prompt cursor-pointer"
                        >
                          <span className="shrink-0 text-sm">🎙️</span>
                          <span className="truncate italic group-hover/prompt:text-white">
                            &ldquo;Oga my brake dey do kpa-kpa sound...&rdquo;
                          </span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setChatInput("Find ceramic front pads for a 2018 Toyota Camry in Lagos");
                            handleTabSelect('axon');
                          }}
                          className="w-full text-left p-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-cyan-500/40 text-[11px] text-slate-300 transition-all flex items-center gap-2 group/prompt cursor-pointer"
                        >
                          <span className="shrink-0 text-sm">🔍</span>
                          <span className="truncate italic group-hover/prompt:text-white">
                            &ldquo;Ceramic pads for 2018 Camry in Lagos&rdquo;
                          </span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setChatInput("Why does my 2015 Corolla jerk when shifting to Reverse?");
                            handleTabSelect('axon');
                          }}
                          className="w-full text-left p-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-cyan-500/40 text-[11px] text-slate-300 transition-all flex items-center gap-2 group/prompt cursor-pointer"
                        >
                          <span className="shrink-0 text-sm">⚡</span>
                          <span className="truncate italic group-hover/prompt:text-white">
                            &ldquo;2015 Corolla jerks on reverse gear&rdquo;
                          </span>
                        </button>
                      </div>

                      {/* Capabilities Tag Strip */}
                      <div className="flex items-center gap-1.5 flex-wrap pt-1 text-[10px] text-slate-400">
                        <span className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10">5 Dialects</span>
                        <span className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10">OEM Cross-Check</span>
                        <span className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10">&lt; 1s Response</span>
                      </div>
                    </div>

                    <div className="pt-3.5 mt-3 border-t border-white/10 relative z-10">
                      <button
                        type="button"
                        onClick={() => handleTabSelect('axon')}
                        className="w-full py-2.5 rounded-xl bg-white hover:bg-slate-100 text-[#060D1E] text-xs font-black transition-all flex items-center justify-center gap-1.5 shadow-lg shadow-white/10 active:scale-95 cursor-pointer"
                      >
                        <span>Chat with Axon AI</span>
                        <ArrowRight className="size-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Module 3: Direct WhatsApp Commerce & Buyer Protection (25% / col-span-1) */}
                  <div className="rounded-3xl bg-gradient-to-br from-[#091833] via-[#071328] to-[#040A18] border border-emerald-500/20 p-5 sm:p-6 text-white shadow-xl flex flex-col justify-between relative overflow-hidden group">
                    <div className="absolute bottom-0 right-0 size-40 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />
                    
                    <div className="relative z-10 space-y-3">
                      {/* Badge and Handoff SLA */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-300 bg-emerald-950/50 border border-emerald-500/20 px-2.5 py-0.5 rounded-full">
                          <MessageCircle className="size-3 text-emerald-400" />
                          <span>WhatsApp Commerce</span>
                        </div>
                        <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950/40 px-2 py-0.5 rounded-full border border-emerald-500/20 font-bold">
                          Direct Handoff
                        </span>
                      </div>

                      <div>
                        <h3 className="font-display font-extrabold text-lg sm:text-xl text-white tracking-tight">
                          Direct WhatsApp Order
                        </h3>
                        <p className="text-xs text-slate-300 leading-relaxed mt-1">
                          Connect instantly with verified depot owners in Ladipo, Abossey Okai &amp; Trade Fair. Request live photos, verify chassis fitment, and checkout securely.
                        </p>
                      </div>

                      {/* Feature Checklist for buyers */}
                      <ul className="space-y-2 text-xs text-slate-300 pt-1">
                        <li className="flex items-start gap-2">
                          <Check className="size-3.5 text-emerald-400 shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-white block text-[11px]">Live Part Photo Verification</span>
                            <span className="text-[10px] text-slate-400">Inspect Tokunbo &amp; OEM spares via video before dispatch</span>
                          </div>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="size-3.5 text-emerald-400 shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-white block text-[11px]">100% Exact-Fit Guarantee</span>
                            <span className="text-[10px] text-slate-400">Pre-populated chassis number &amp; part SKU cross-check</span>
                          </div>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="size-3.5 text-emerald-400 shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-white block text-[11px]">Escrow Protection</span>
                            <span className="text-[10px] text-slate-400">Payment released only when your mechanic approves fitment</span>
                          </div>
                        </li>
                      </ul>

                      {/* Stats Bar */}
                      <div className="grid grid-cols-3 gap-1.5 p-2 rounded-xl bg-white/[0.04] border border-white/10 text-center">
                        <div>
                          <span className="text-white font-black text-xs block">100%</span>
                          <span className="text-[9px] text-slate-400 uppercase">Fitment</span>
                        </div>
                        <div>
                          <span className="text-emerald-400 font-black text-xs block">&lt; 15 min</span>
                          <span className="text-[9px] text-slate-400 uppercase">Response</span>
                        </div>
                        <div>
                          <span className="text-cyan-400 font-black text-xs block">Escrow</span>
                          <span className="text-[9px] text-slate-400 uppercase">Security</span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3.5 mt-3 border-t border-white/10 relative z-10 space-y-2">
                      <button
                        type="button"
                        onClick={() => {
                          handleTabSelect('shop');
                        }}
                        className="w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-black transition-all flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-500/20 active:scale-95 cursor-pointer"
                      >
                        <MessageCircle className="size-3.5" />
                        <span>Browse &amp; Order on WhatsApp</span>
                      </button>
                      <p className="text-[10px] text-center text-slate-400">
                        Are you a parts merchant? <Link href="/signup" className="text-emerald-400 hover:underline">Partner with us &rarr;</Link>
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* ========================================================================= */}
              {/* 5. VALUE & TRUST STRIP (4 PILLARS ON DARK NAVY) */}
              {/* ========================================================================= */}
              <section className="px-3 sm:px-6 max-w-[1500px] mx-auto w-full">
                <h2 className="sr-only">Why Choose Parts4Drive Guaranteed Fitment &amp; Logistics</h2>
                <div className="rounded-3xl bg-[#060D1E] border border-white/10 p-6 sm:p-8 text-white shadow-xl">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="flex items-start gap-3.5">
                      <div className="size-10 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center shrink-0">
                        <ShieldCheck className="size-5 text-[#38BDF8]" />
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-sm text-white">100% Fitment Guarantee</h3>
                        <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                          If the part does not fit your verified vehicle, returns are free with zero hassle.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3.5">
                      <div className="size-10 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center shrink-0">
                        <CreditCard className="size-5 text-[#38BDF8]" />
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-sm text-white">Escrow-Protected Payments</h3>
                        <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                          Funds are only released to vendors after you inspect and accept the parts.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3.5">
                      <div className="size-10 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center shrink-0">
                        <Store className="size-5 text-[#38BDF8]" />
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-sm text-white">Verified Depot Merchants</h3>
                        <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                          Every supplier in Ladipo, Abossey Okai & regional hubs is physical-depot audited.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3.5">
                      <div className="size-10 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center shrink-0">
                        <Truck className="size-5 text-[#38BDF8]" />
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-sm text-white">Sub-Saharan Logistics</h3>
                        <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                          Same-day delivery in major capitals, 48-hour express transit across borders.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* ========================================================================= */}
              {/* 6. CONCIERGE SOURCING DESK CTA */}
              {/* ========================================================================= */}
              <section className="px-3 sm:px-6 max-w-[1500px] mx-auto w-full pb-4">
                <div className="rounded-3xl bg-gradient-to-r from-[#0A1B4A] to-[#142D73] border border-white/10 p-6 sm:p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="space-y-2 text-center md:text-left max-w-2xl">
                    <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-emerald-300">
                      <CheckCircle2 className="size-3.5" />
                      <span>Concierge Sourcing Desk</span>
                    </div>
                    <h2 className="font-display font-extrabold text-xl sm:text-2xl text-white">
                      Can&apos;t Find the Exact Part for Your Vehicle?
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-200 leading-relaxed">
                      Submit your part requirement, OEM number, or chassis plate photo. Our regional sourcing desk searches over 10,000+ physical depot merchants in Ladipo, Abossey Okai, and cross-border exchanges with a guaranteed response within 30 minutes.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => setShowRequestPartModal(true)}
                      className="w-full sm:w-auto px-6 py-3 rounded-full bg-white hover:bg-slate-100 text-[#0A1B4A] font-extrabold text-xs shadow-md transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="size-4" />
                      <span>Submit Part Request</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTabSelect('axon')}
                      className="w-full sm:w-auto px-6 py-3 rounded-full border border-white/20 bg-white/10 hover:bg-white/15 text-white font-extrabold text-xs transition-colors flex items-center justify-center gap-2"
                    >
                      <Wrench className="size-4 text-emerald-300" />
                      <span>Parts Assistant</span>
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: SHOP & CATALOG MATRIX */}
        {/* ========================================================================= */}
        {activeTab === 'shop' && (
          <ShopPageReplica
            products={products}
            isLoading={loadingProducts}
            selectedCountry={selectedCountry}
            onProductClick={(product) => handleProductClick(product)}
            onVendorClick={handleOpenVendorStore}
            onVinSearch={(vin) => {
              setVinInput(vin);
              handleDecodeVin(vin);
            }}
            onRequestPart={() => setShowRequestPartModal(true)}
            onChatWithEve={() => handleTabSelect('axon')}
            activeVehicle={activeVehicle}
            getWhatsAppHandoffUrl={getWhatsAppHandoffUrl}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
          />
        )}

        {/* ========================================================================= */}
        {/* TAB 3: AXON AI ASSISTANT (CONTAINERIZED FRAME & STICKY DOCK) */}
        {/* ========================================================================= */}
        {activeTab === 'axon' && (
          <div className="max-w-4xl mx-auto flex flex-col h-[calc(100dvh-130px)] rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#13161C] shadow-xl overflow-hidden sticky top-20">
            {/* Inner Header: Title, Online Pulse, Active Garage Vehicle Selector, Country Indicator */}
            <div className="p-3 sm:p-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-[#10131A]/70 flex flex-wrap items-center justify-between gap-2.5 shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="size-9 rounded-2xl overflow-hidden border border-blue-500/20 bg-blue-900/10 flex items-center justify-center shrink-0 shadow-xs">
                  <img src="/logo/axon-ai.png" alt="Axon AI" className="size-full object-cover" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="font-display font-bold text-sm sm:text-base text-slate-900 dark:text-white truncate">
                      Axon AI Assistant
                    </h2>
                    {/* Live Online Pulse */}
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full shrink-0">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                      </span>
                      <span>Online</span>
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-zinc-400 truncate">
                    Automotive fitment, diagnostic &amp; vendor quotes
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap shrink-0">
                {/* Active Garage Vehicle Selector */}
                <button
                  type="button"
                  onClick={() => setActiveTab('garage')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-[#181C24] border border-slate-200 dark:border-slate-800 text-xs text-slate-700 dark:text-zinc-300 hover:border-blue-600 transition-colors shadow-xs"
                  title="Select or manage garage vehicle"
                >
                  <Car className="size-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <span className="truncate max-w-[130px] sm:max-w-[160px]">
                    {activeVehicle ? `${activeVehicle.year} ${activeVehicle.make} ${activeVehicle.model}` : 'Select Garage Car'}
                  </span>
                  <span className="size-1.5 rounded-full bg-blue-600"></span>
                </button>
              </div>
            </div>

            {/* Message History & Pre-Message Starters */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0 scroll-smooth">
              {chatMessages.length === 0 ? (
                /* Axon Welcome & Diagnostic Hero Card */
                <div className="h-full flex flex-col justify-center items-center py-6 px-3 sm:px-6 text-center max-w-xl mx-auto space-y-5 animate-in fade-in duration-300">
                  <div className="size-16 rounded-3xl overflow-hidden border border-blue-500/20 bg-blue-900/10 flex items-center justify-center shadow-md p-1">
                    <img src="/logo/axon-ai.png" alt="Axon AI Specialist" className="size-full object-cover rounded-2xl" />
                  </div>

                  <div className="space-y-1.5">
                    <h3 className="font-display font-extrabold text-lg sm:text-xl text-slate-900 dark:text-white tracking-tight">
                      Axon AI Automotive Specialist
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 dark:text-zinc-400 max-w-md mx-auto leading-relaxed">
                      Instant vehicle fitment diagnostics, European/Asian VIN decoding, and direct supplier quotes across Nigeria and Ghana.
                    </p>
                  </div>

                  {/* Pre-Message suggestion chips */}
                  <div className="w-full flex flex-wrap justify-center gap-2 pt-2">
                    {[
                      "Decode European VIN: WVWZZZ3CZGE110616",
                      "Find OEM Ceramic Brake Pads for Passat",
                      "Search 140A Heavy-Duty Alternators",
                      "Check suspension & strut pairs in Lagos",
                      "Diagnose squeaking brake sound with photo"
                    ].map((chip) => (
                      <button
                        key={chip}
                        type="button"
                        onClick={() => handleSendMessage(chip)}
                        className="px-3.5 py-2 rounded-2xl text-xs font-medium bg-slate-100 dark:bg-[#181C24] hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:text-blue-600 dark:hover:text-blue-400 border border-slate-200 dark:border-slate-800 transition-all text-left shadow-2xs"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {chatMessages.map((msg) => {
                    const isUser = msg.sender === 'user'
                    return (
                      <div
                        key={msg.id}
                        className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-full`}
                      >
                        <div
                          className={`p-3.5 rounded-2xl max-w-[90%] sm:max-w-[80%] text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words ${
                            isUser
                              ? 'bg-blue-600 text-white font-medium rounded-tr-none shadow-sm'
                              : 'bg-slate-100 dark:bg-[#181C24] text-slate-900 dark:text-zinc-200 rounded-tl-none border border-slate-200 dark:border-slate-800'
                          }`}
                        >
                          {msg.content}
                        </div>

                        {/* Staged or Transmitted Image Preview */}
                        {msg.imageUrl && (
                          <div className="mt-2 max-w-[240px] rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm">
                            <img src={msg.imageUrl} alt="Attached component" className="w-full h-auto object-cover max-h-48" />
                          </div>
                        )}

                        {/* Decoded Vehicle Telemetry Card */}
                        {msg.decodedVehicle && (
                          <div className="mt-2.5 w-full bg-slate-50 dark:bg-[#10131A] rounded-2xl p-3 border border-blue-500/30 bg-gradient-to-br from-blue-500/5 dark:from-blue-950/20 to-transparent space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <Car className="size-4 text-blue-600 dark:text-blue-400" />
                                <span className="text-xs font-bold text-slate-900 dark:text-white">
                                  {msg.decodedVehicle.year} {msg.decodedVehicle.make} {msg.decodedVehicle.model}
                                </span>
                              </div>
                              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <ShieldCheck className="size-3 text-emerald-600 dark:text-emerald-400" />
                                VIN Verified
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600 dark:text-zinc-400 pt-1 border-t border-slate-200 dark:border-slate-800">
                              <div>
                                <span className="text-slate-400 dark:text-zinc-500">VIN:</span> <span className="text-slate-800 dark:text-zinc-200 font-mono font-semibold">{msg.decodedVehicle.vin}</span>
                              </div>
                              <div>
                                <span className="text-slate-400 dark:text-zinc-500">Body:</span> <span className="text-slate-800 dark:text-zinc-200">{msg.decodedVehicle.bodyClass || msg.decodedVehicle.trim || 'Standard'}</span>
                              </div>
                              <div>
                                <span className="text-slate-400 dark:text-zinc-500">Assembly:</span> <span className="text-slate-800 dark:text-zinc-200">{[msg.decodedVehicle.plantCity, msg.decodedVehicle.plantCountry].filter(Boolean).join(', ') || 'Global Plant'}</span>
                              </div>
                              <div>
                                <span className="text-slate-400 dark:text-zinc-500">Fuel:</span> <span className="text-slate-800 dark:text-zinc-200">{msg.decodedVehicle.fuelType || 'Gasoline'}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Candidate Products Grid inside Axon response */}
                        {msg.products && msg.products.length > 0 && (
                          <div className="mt-2.5 w-full bg-slate-50 dark:bg-[#10131A] rounded-2xl p-3 border border-slate-200 dark:border-slate-800 space-y-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
                                Recommended Components
                              </span>
                              <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">
                                Exact Car Match
                              </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {msg.products.slice(0, 4).map((p) => {
                                const prodImg = p.imageUrl || (p as any).image_url || ((p as any).images && (p as any).images[0]) || null;
                                return (
                                  <div
                                    key={p.id}
                                    onClick={() => handleProductClick(p)}
                                    className="bg-white dark:bg-zinc-900 rounded-xl p-2 sm:p-2.5 border border-slate-200 dark:border-slate-800 hover:border-blue-500/50 cursor-pointer transition-all shadow-xs flex items-center gap-2.5 group"
                                  >
                                    {/* Dedicated Product Image Holder */}
                                    <div className="size-14 sm:size-16 rounded-lg bg-slate-100 dark:bg-slate-800 shrink-0 overflow-hidden relative border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-center">
                                      {prodImg ? (
                                        <img
                                          src={prodImg}
                                          alt={p.name || 'Auto Part'}
                                          className="size-full object-cover group-hover:scale-105 transition-transform"
                                          loading="lazy"
                                          onError={(e) => {
                                            (e.currentTarget as HTMLElement).style.display = 'none';
                                            const parent = e.currentTarget.parentElement;
                                            if (parent && !parent.querySelector('.img-fallback')) {
                                              const fb = document.createElement('span');
                                              fb.className = 'img-fallback text-base sm:text-lg';
                                              fb.textContent = '⚙️';
                                              parent.appendChild(fb);
                                            }
                                          }}
                                        />
                                      ) : (
                                        <Package className="size-6 text-slate-400 dark:text-zinc-500" />
                                      )}
                                    </div>

                                    {/* Component Details */}
                                    <div className="min-w-0 flex-1">
                                      <p className="text-[10px] text-slate-500 dark:text-zinc-400 truncate font-medium">
                                        {p.seller?.storeName || p.seller?.name || (p as any).vendor?.name || 'Verified Merchant'}
                                      </p>
                                      <h4 className="font-bold text-xs text-slate-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                        {p.name || p.title}
                                      </h4>
                                      <div className="flex items-center justify-between pt-1 gap-1">
                                        <span className="font-bold text-xs text-blue-600 dark:text-blue-400 truncate">
                                          {p.priceFormatted || formatPrice(p.price, p.currency, p.seller?.country || p.country)}
                                        </span>
                                        <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded shrink-0">
                                          In Stock
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                  {chatLoading && (
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 p-2">
                      <Loader2 className="size-4 animate-spin text-blue-600" />
                      <span>Axon is analyzing part tolerances and vendor quotes...</span>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </>
              )}
            </div>

            {/* Chat Input Dock: Stickily pinned at the bottom of the container */}
            <div className="sticky bottom-0 z-30 p-2.5 sm:p-3 bg-white/95 dark:bg-[#13161C]/95 backdrop-blur-md border-t border-slate-150 dark:border-slate-800">
              {/* Local Staged Image Attachment using shadcn Attachment */}
              {chatAttachment && (
                <div className="mb-2.5">
                  <Attachment state="idle" size="sm" orientation="horizontal">
                    <AttachmentMedia variant="image" src={chatAttachment.previewUrl} alt={chatAttachment.name} />
                    <AttachmentContent>
                      <AttachmentTitle>{chatAttachment.name}</AttachmentTitle>
                      <AttachmentDescription>{chatAttachment.sizeKb} KB • Staged Image</AttachmentDescription>
                    </AttachmentContent>
                    <AttachmentActions>
                      <AttachmentAction
                        variant="remove"
                        onClick={() => {
                          setChatAttachment(null)
                          if (chatFileInputRef.current) chatFileInputRef.current.value = ''
                        }}
                        aria-label="Remove image attachment"
                      />
                    </AttachmentActions>
                  </Attachment>
                </div>
              )}

              <form
                onSubmit={handleSendMessage}
                className="w-full flex items-center gap-1.5 sm:gap-2 bg-slate-100 dark:bg-[#181C24] rounded-full border border-slate-200 dark:border-slate-700/80 px-2 py-1.5 shadow-xs"
              >
                {/* + Attachment Button */}
                <button
                  type="button"
                  onClick={() => chatFileInputRef.current?.click()}
                  title="Upload part photo for visual fitment"
                  className="size-8 sm:size-9 rounded-full bg-white dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-zinc-200 flex items-center justify-center shrink-0 transition-colors shadow-xs"
                >
                  <Plus className="size-4" />
                </button>

                {/* Chat Input Field */}
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask Axon about fitment, parts, vendors..."
                  className="flex-1 min-w-0 bg-transparent border-none outline-none text-xs sm:text-sm text-slate-900 dark:text-zinc-100 placeholder:text-slate-400 dark:placeholder:text-zinc-500 px-1 sm:px-2"
                />

                {/* Voice Mic Button */}
                <button
                  type="button"
                  onClick={() => setIsVoiceSearchOpen(true)}
                  title="Speak voice inquiry"
                  className="size-8 sm:size-9 rounded-full bg-white dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-zinc-200 hover:text-blue-600 dark:hover:text-blue-400 flex items-center justify-center shrink-0 transition-colors shadow-xs"
                >
                  <Mic className="size-4" />
                </button>

                {/* Send Button */}
                <button
                  type="submit"
                  disabled={(!chatInput.trim() && !chatAttachment) || chatLoading}
                  className="size-8 sm:size-9 rounded-full bg-[#0A1B4A] hover:bg-[#0f2560] text-white font-bold flex items-center justify-center shrink-0 shadow-md disabled:opacity-40 transition-all active:scale-95"
                >
                  <Send className="size-3.5" />
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 4: VIRTUAL GARAGE (AUTHENTICATED ONLY) */}
        {/* ========================================================================= */}
        {activeTab === 'garage' && (
          <div className="space-y-6 max-w-4xl mx-auto">
            {(!user || !(user.email_verified === 1 || user.email_verified === true || user.emailVerified === true)) ? (
              /* Gated Preview for Guest or Unverified Buyer */
              <div className="bg-white dark:bg-[#13161C] rounded-3xl p-6 sm:p-8 border border-slate-200/60 dark:border-slate-800 text-center space-y-4 shadow-[0_10px_30px_-5px_rgba(10,27,74,0.05),0_20px_40px_-15px_rgba(37,99,235,0.07)]">
                <div className="size-14 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto">
                  <Car className="size-7" />
                </div>
                <div className="space-y-1.5 max-w-md mx-auto">
                  <h2 className="font-display font-bold text-lg sm:text-xl text-[#0A1B4A] dark:text-white">
                    {user ? 'Verify Your Email to Access Your Garage' : 'Virtual Garage Requires Sign In'}
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                    {user
                      ? 'Please enter the 6-digit verification code sent to your email to verify your account and access your saved vehicle garage.'
                      : 'Register or sign in to save your vehicles, decode VINs, and filter guaranteed exact car fitment matching across all verified vendors.'}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                  {user ? (
                    <button
                      type="button"
                      onClick={() => openEmailVerification(user.email)}
                      className="w-full sm:w-auto px-6 py-2.5 rounded-full bg-[#0A1B4A] dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-semibold text-xs shadow-md hover:bg-[#0f2560] transition-all"
                    >
                      Enter 6-Digit OTP Code
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowAuthModal(true)}
                        className="w-full sm:w-auto px-6 py-2.5 rounded-full bg-[#0A1B4A] dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-semibold text-xs shadow-md hover:bg-[#0f2560] transition-all"
                      >
                        Sign In to Virtual Garage
                      </button>
                      <button
                        type="button"
                        onClick={() => router.push('/signup')}
                        className="w-full sm:w-auto px-6 py-2.5 rounded-full border border-[#0A1B4A] dark:border-slate-700 text-[#0A1B4A] dark:text-slate-200 font-semibold text-xs bg-transparent hover:bg-[#0A1B4A]/5 dark:hover:bg-slate-800 transition-all"
                      >
                        Create Account
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* Full Garage Screen for Logged-In User */
              <>
                {/* User Greeting & Status Strip */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="size-11 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-display font-extrabold text-base">
                      {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Welcome to your garage,</p>
                      <h2 className="font-display font-bold text-base sm:text-lg text-[#0A1B4A] dark:text-white">
                        {user.name}
                      </h2>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleOpenAddVehicle}
                    className="h-9 px-5 rounded-full bg-[#0A1B4A] dark:bg-blue-600 hover:bg-[#0f2560] dark:hover:bg-blue-700 text-white font-semibold text-xs flex items-center gap-1.5 shadow-xs transition-all"
                  >
                    <Plus className="size-4" />
                    <span>Add Vehicle</span>
                  </button>
                </div>

                {/* Active Garage Vehicle Telemetry Banner */}
                <div className="p-4 rounded-3xl bg-white dark:bg-[#13161C] border border-slate-200/60 dark:border-slate-800 shadow-xs flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <Car className="size-5 text-blue-600 dark:text-blue-400 shrink-0" />
                    <div className="truncate">
                      <span className="text-xs text-slate-500 dark:text-slate-400">Active Vehicle: </span>
                      <span className="text-xs font-bold text-[#0A1B4A] dark:text-white">
                        {activeVehicle ? `${activeVehicle.year} ${activeVehicle.make} ${activeVehicle.model} ${activeVehicle.trim || ''}` : 'None selected'}
                      </span>
                    </div>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 text-[10px] font-bold uppercase tracking-wider shrink-0">
                    Car Matched
                  </span>
                </div>

                {/* Instant VIN Decoder Card */}
                <div className="p-5 sm:p-6 rounded-3xl bg-white dark:bg-[#13161C] border border-slate-200/60 dark:border-slate-800 shadow-xs space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-display font-bold text-sm text-[#0A1B4A] dark:text-white">NHTSA Vehicle VIN Decoder</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Decode official chassis specs and add to your garage</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={vinInput}
                      onChange={(e) => setVinInput(e.target.value.toUpperCase())}
                      placeholder="17-character VIN"
                      maxLength={17}
                      className="flex-1 px-4 py-2.5 rounded-full bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-xs font-mono text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleDecodeVin()}
                      disabled={vinLoading || !vinInput.trim()}
                      className="px-5 py-2.5 rounded-full bg-[#0A1B4A] dark:bg-blue-600 hover:bg-[#0f2560] dark:hover:bg-blue-700 text-white font-semibold text-xs flex items-center justify-center gap-1.5 disabled:opacity-40 transition-all shrink-0 shadow-xs"
                    >
                      {vinLoading ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
                      <span>Decode &amp; Save</span>
                    </button>
                  </div>
                </div>

                {/* Saved Vehicles List */}
                <div className="space-y-3">
                  <h3 className="font-display font-bold text-sm sm:text-base text-[#0A1B4A] dark:text-white">
                    Saved Garage Vehicles ({garage.length})
                  </h3>
                  <div className="space-y-2.5">
                    {garage.length === 0 ? (
                      <div className="p-8 text-center rounded-3xl bg-white dark:bg-[#13161C] border border-slate-200/60 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-xs shadow-xs">
                        No vehicles saved in your garage yet. Add a vehicle or decode a VIN above to enable 100% exact car fitment matching.
                      </div>
                    ) : (
                      garage.map((car) => {
                        const isActive = car.id === activeVehicleId
                        return (
                          <div
                            key={car.id}
                            onClick={() => setActiveVehicleId(car.id)}
                            className={`p-3.5 rounded-2xl transition-all cursor-pointer flex items-center justify-between border ${
                              isActive
                                ? 'bg-blue-50/50 dark:bg-blue-950/40 border-blue-600 shadow-xs'
                                : 'bg-white dark:bg-[#13161C] border-slate-200/60 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 shadow-xs'
                            }`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <VehicleBrandLogo
                                brand={car.make}
                                size="sm"
                                variant="badge"
                                className="shrink-0"
                              />
                              <div className="min-w-0">
                                <p className="font-display font-bold text-xs sm:text-sm text-[#0A1B4A] dark:text-white truncate">
                                  {car.year} {car.make} {car.model}
                                </p>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                                  {car.trim || 'Standard Trim'} • {car.engine || 'Engine'} {car.vin ? `• VIN: ${car.vin}` : ''}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              {isActive ? (
                                <span className="px-2.5 py-0.5 rounded-full bg-[#0A1B4A] dark:bg-blue-600 text-white font-bold text-[10px]">
                                  Active
                                </span>
                              ) : (
                                <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold hover:text-[#0A1B4A] dark:hover:text-white">
                                  Select
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.stopPropagation()
                                  const carToDeleteId = car.id
                                  const next = garage.filter((v) => v.id !== carToDeleteId)
                                  setGarage(next)
                                  if (activeVehicleId === carToDeleteId) {
                                    setActiveVehicleId(next[0]?.id || '')
                                  }
                                  if (typeof window !== 'undefined') {
                                    localStorage.setItem('p4d_saved_garage', JSON.stringify(next))
                                  }
                                  try {
                                    const apiBase = typeof window !== 'undefined' && window.location.hostname.endsWith('parts4drive.com') ? '' : (process.env.NEXT_PUBLIC_P4D_URL || 'https://parts4drive.com')
                                    const userId = user?.id || (typeof localStorage !== 'undefined' ? localStorage.getItem('p4d_guest_user_id') : '') || 'guest_buyer'
                                    await fetch(`${apiBase}/api/v1/garage/${encodeURIComponent(carToDeleteId)}?userId=${encodeURIComponent(userId)}`, {
                                      method: 'DELETE'
                                    })
                                  } catch (err) {
                                    console.warn('Failed to delete car from cloud garage:', err)
                                  }
                                }}
                                title="Delete vehicle"
                                className="p-1.5 rounded-full text-slate-400 hover:text-red-600 hover:bg-slate-100 transition-colors"
                              >
                                <Trash2 className="size-3.5" />
                              </button>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 5: ORDERS & INQUIRIES LEDGER */}
        {/* ========================================================================= */}
        {activeTab === 'orders' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div>
              <h2 className="font-display font-bold text-xl text-[#0A1B4A] dark:text-white">
                Orders &amp; Vendor Inquiries
              </h2>
              <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">
                Direct vendor dispatches, tracking notes, and quotes
              </p>
            </div>

            {loadingOrders ? (
              <div className="py-16 text-center">
                <Loader2 className="size-8 text-blue-600 animate-spin mx-auto mb-2" />
                <p className="text-xs text-slate-500">Loading order history...</p>
              </div>
            ) : userOrders.length === 0 ? (
              <div className="bg-white dark:bg-[#13161C] rounded-3xl p-8 border border-slate-200/60 dark:border-slate-800 text-center space-y-3 shadow-xs">
                <Package className="size-12 text-slate-400 mx-auto" />
                <h3 className="font-display font-bold text-base text-[#0A1B4A] dark:text-white">No active orders found</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                  When you initiate orders or WhatsApp inquiries with verified vendors, they will appear in your ledger here.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab(user ? 'shop' : 'home')}
                  className="px-6 py-2.5 rounded-full bg-[#0A1B4A] dark:bg-blue-600 hover:bg-[#0f2560] dark:hover:bg-blue-700 text-white font-semibold text-xs shadow-md transition-colors"
                >
                  Explore Parts Catalog
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {userOrders.map((order) => (
                  <div
                    key={order.id}
                    className="p-4 rounded-2xl bg-white dark:bg-[#13161C] border border-slate-200/60 dark:border-slate-800 flex items-center justify-between shadow-xs"
                  >
                    <div>
                      <p className="font-display font-bold text-sm text-[#0A1B4A] dark:text-white">
                        Order #{order.id.slice(-6)}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{order.status || 'Dispatched'}</p>
                    </div>
                    <span className="font-display font-bold text-sm text-[#0A1B4A] dark:text-white">
                      ₦{order.total?.toLocaleString() || '0'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB: COMMERCIAL HUBS & 360° STREET VIEW CONSOLE */}
        {/* ========================================================================= */}
        {activeTab === 'hubs' && (() => {
          const currentCountryObj = getCountryByCode(selectedCountry)
          const countrySpecificHubs = COMMERCIAL_HUBS.filter(
            (h) => h.country.toLowerCase() === (currentCountryObj?.name || '').toLowerCase()
          )
          const displayedHubs = countrySpecificHubs.length > 0 ? countrySpecificHubs : COMMERCIAL_HUBS
          const activeHub = displayedHubs.find(
            (h) => h.name.toLowerCase() === selectedHubName.toLowerCase() || h.id === selectedHubName.toLowerCase()
          ) || displayedHubs[0]

          return (
            <div className="w-full space-y-8 animate-fadeIn">
              {/* Top Banner with Telemetry */}
              <div className="rounded-3xl bg-gradient-to-r from-[#060E24] via-[#091536] to-[#04091A] border border-blue-500/20 p-6 sm:p-8 text-white relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="space-y-3 max-w-2xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-900/60 border border-blue-500/30 text-cyan-300 text-xs font-bold uppercase tracking-wider">
                      <span className="size-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span>Commercial Depot Network &bull; 360&deg; Street View</span>
                    </div>
                    <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
                      {activeHub.name} &bull; {activeHub.city}
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                      {activeHub.address}. Rapid 2-hour depot dispatch across the {activeHub.corridor}.
                    </p>
                  </div>

                  {/* Actions & Metrics */}
                  <div className="flex flex-wrap items-center gap-3 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setShopFilters(prev => ({ ...prev, hub: activeHub.name }))
                        handleTabSelect('shop')
                      }}
                      className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow-lg shadow-blue-600/30 transition-all flex items-center gap-2 active:scale-95 cursor-pointer"
                    >
                      <span>Browse {activeHub.name} Stock</span>
                      <ArrowRight className="size-3.5" />
                    </button>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${activeHub.lat},${activeHub.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors flex items-center gap-1.5"
                    >
                      <Navigation className="size-3.5" />
                      <span>Directions</span>
                    </a>
                    <Link
                      href={`/${selectedCountry.toLowerCase()}/hubs`}
                      className="px-3.5 py-2.5 rounded-xl border border-white/20 hover:bg-white/10 text-slate-300 hover:text-white text-xs font-semibold transition-colors flex items-center gap-1.5"
                      title="Open Dedicated Fullscreen Hubs Portal"
                    >
                      <ExternalLink className="size-3.5" />
                      <span className="hidden sm:inline">Dedicated Hubs Portal</span>
                    </Link>
                  </div>
                </div>

                {/* Hub Quick Stats Ribbon */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 mt-6 border-t border-white/10 text-xs">
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <span className="text-slate-400 block text-[10px] uppercase font-mono">Merchants</span>
                    <strong className="text-white font-bold text-sm sm:text-base">{activeHub.merchantsCount}</strong>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <span className="text-slate-400 block text-[10px] uppercase font-mono">Dispatch SLA</span>
                    <strong className="text-emerald-400 font-bold text-sm sm:text-base">{activeHub.dispatchTime}</strong>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <span className="text-slate-400 block text-[10px] uppercase font-mono">GPS Coordinates</span>
                    <strong className="text-cyan-300 font-mono text-xs">{activeHub.lat.toFixed(4)}°N, {activeHub.lng.toFixed(4)}°E</strong>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <span className="text-slate-400 block text-[10px] uppercase font-mono">Specialty Hub</span>
                    <strong className="text-purple-300 font-bold text-xs truncate block">{activeHub.specialty.split(',')[0]}</strong>
                  </div>
                </div>
              </div>

              {/* Commercial Corridor Switcher Tabs */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
                {displayedHubs.map((hub) => {
                  const isSelected = hub.id === activeHub.id || hub.name === activeHub.name
                  return (
                    <button
                      key={hub.id}
                      type="button"
                      onClick={() => setSelectedHubName(hub.name)}
                      className={`px-4 py-2.5 rounded-2xl text-xs font-bold transition-all shrink-0 flex items-center gap-2 border cursor-pointer ${
                        isSelected
                          ? 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-600/30'
                          : 'bg-white dark:bg-[#13161C] text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800 hover:border-blue-400'
                      }`}
                    >
                      <span className="text-base">{hub.flag}</span>
                      <span>{hub.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${
                        isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                      }`}>
                        {hub.city.split(',')[0]}
                      </span>
                    </button>
                  )
                })}
              </div>

              {/* Primary Interactive 360° Street View & GIS Console */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <Compass className="size-4 text-cyan-500" />
                    <span>Live 360° Street View Console — {activeHub.name}</span>
                  </h2>
                  <span className="text-xs text-slate-500 font-mono">Pan &bull; Pitch &bull; Zoom &bull; Multi-layer GIS</span>
                </div>

                <OpenStreetViewer
                  hub={activeHub}
                  height="h-[460px] sm:h-[540px]"
                  showDetails={true}
                />
              </div>

              {/* Regional Hub Cards Grid with Street View Viewports */}
              <div className="space-y-4 pt-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                    Commercial Depots &amp; Hubs in {currentCountryObj?.name || selectedCountry}
                  </h2>
                  <span className="text-xs text-slate-500">{displayedHubs.length} Strategic Corridors with Live Street View</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {displayedHubs.map((hub) => {
                    const isSelected = hub.id === activeHub.id || hub.name === activeHub.name
                    return (
                      <div
                        key={hub.id}
                        className={`rounded-3xl p-5 space-y-4 flex flex-col justify-between transition-all border ${
                          isSelected
                            ? 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-500 shadow-xl ring-2 ring-blue-500/20'
                            : 'bg-white dark:bg-[#13161C] border-slate-200/80 dark:border-slate-800 hover:border-blue-400/60 shadow-sm'
                        }`}
                      >
                        <div>
                          <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                            <div className="flex items-center gap-2.5">
                              <span className="text-2xl">{hub.flag}</span>
                              <div>
                                <h3 className="font-bold text-sm text-slate-900 dark:text-white">{hub.name}</h3>
                                <p className="text-xs text-slate-500">{hub.city}</p>
                              </div>
                            </div>
                            <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/70 text-blue-700 dark:text-blue-300 font-mono text-xs font-bold">
                              {hub.merchantsCount}
                            </span>
                          </div>

                          {/* Embedded Street View Viewport */}
                          <div className="my-3">
                            <OpenStreetViewer
                              hub={hub}
                              height="h-[180px]"
                              showDetails={false}
                              compact={true}
                            />
                          </div>

                          <div className="space-y-1.5 text-xs text-slate-600 dark:text-slate-300">
                            <div className="flex items-start gap-1.5">
                              <Store className="size-3.5 text-blue-500 shrink-0 mt-0.5" />
                              <span className="text-slate-500 dark:text-slate-400 line-clamp-2">{hub.address}</span>
                            </div>
                            <div className="flex items-center justify-between text-[11px] font-mono pt-1 text-slate-500">
                              <span>SLA: <strong className="text-emerald-600 dark:text-emerald-400">{hub.dispatchTime}</strong></span>
                              <span>Corridor: {hub.corridor.split('/')[0]}</span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedHubName(hub.name)
                              setShopFilters(prev => ({ ...prev, hub: hub.name }))
                              handleTabSelect('shop')
                            }}
                            className="py-2 px-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold text-center transition-colors flex items-center justify-center gap-1 shadow-sm cursor-pointer"
                          >
                            <span>Browse Stock</span>
                            <ArrowRight className="size-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedHubName(hub.name)}
                            className="py-2 px-3 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs font-semibold text-center transition-colors flex items-center justify-center gap-1 cursor-pointer"
                          >
                            <Compass className="size-3" />
                            <span>Street View</span>
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )
        })()}

        {/* ========================================================================= */}
        {/* TAB 6: PROFILE & PREFERENCES */}
        {/* ========================================================================= */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div>
              <h1 className="font-display font-bold text-xl text-[#0A1B4A] dark:text-white">
                Buyer Profile &amp; Preferences
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Manage your account credentials, regional hub, and vehicle telemetry
              </p>
            </div>

            {user ? (
              <div className="space-y-4">
                {/* User Card */}
                <div className="p-5 rounded-3xl bg-white dark:bg-[#13161C] border border-slate-200/60 dark:border-slate-800 flex items-center gap-4 shadow-xs">
                  <div className="size-14 rounded-full bg-[#0A1B4A] dark:bg-blue-600 text-white flex items-center justify-center font-display font-extrabold text-xl shrink-0">
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display font-bold text-base text-[#0A1B4A] dark:text-white truncate">{user.name}</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="size-2 rounded-full bg-emerald-500"></span>
                      <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-semibold">Verified Account</span>
                    </div>
                  </div>
                </div>

                {/* Quick Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-2xl bg-white dark:bg-[#13161C] border border-slate-200/60 dark:border-slate-800 shadow-xs">
                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Garage Vehicles</p>
                    <p className="font-display font-bold text-xl text-[#0A1B4A] dark:text-white mt-1">{garage.length}</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-white dark:bg-[#13161C] border border-slate-200/60 dark:border-slate-800 shadow-xs">
                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Orders &amp; Inquiries</p>
                    <p className="font-display font-bold text-xl text-[#0A1B4A] dark:text-white mt-1">{userOrders.length}</p>
                  </div>
                </div>

                {/* Regional Preferences Card */}
                <div className="p-4 rounded-2xl bg-white dark:bg-[#13161C] border border-slate-200/60 dark:border-slate-800 space-y-3 shadow-xs">
                  <h4 className="font-display font-bold text-sm text-[#0A1B4A] dark:text-white">Marketplace Regional Hub</h4>
                  <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
                    <span>Active Hub:</span>
                    <span className="font-bold text-[#0A1B4A] dark:text-white">West Africa (Nigeria &amp; Ghana)</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
                    <span>Fitment Standard:</span>
                    <span className="font-bold text-emerald-700 dark:text-emerald-400">100% Fit Guaranteed</span>
                  </div>
                </div>

                {/* Sign Out Button */}
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full py-3 rounded-full bg-white dark:bg-slate-900 border border-red-200 dark:border-red-900/60 hover:bg-red-50 dark:hover:bg-red-950/40 text-red-600 dark:text-red-400 font-semibold text-xs flex items-center justify-center gap-2 transition-colors shadow-xs"
                >
                  <LogOut className="size-4" />
                  <span>Sign Out of Parts4Drive</span>
                </button>
              </div>
            ) : (
              <div className="bg-white dark:bg-[#13161C] rounded-3xl p-6 border border-slate-200/60 dark:border-slate-800 text-center space-y-3 shadow-xs">
                <User className="size-10 text-slate-400 mx-auto" />
                <h3 className="font-display font-bold text-sm text-[#0A1B4A] dark:text-white">Guest Account</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                  Sign in or create an account to access order history, garage vehicles, and custom quotes.
                </p>
                <button
                  type="button"
                  onClick={() => setShowAuthModal(true)}
                  className="px-6 py-2.5 rounded-full bg-[#0A1B4A] dark:bg-blue-600 hover:bg-[#0f2560] dark:hover:bg-blue-700 text-white font-semibold text-xs shadow-xs transition-colors"
                >
                  Sign In / Register
                </button>
              </div>
            )}
          </div>
        )}

        {/* Marketplace Official Brand Footer (Rendered ONLY on Home Page) */}
        {activeTab === 'home' && (
          <MarketplaceFooter
            country={selectedCountry}
            onCountryClick={() => setShowCountryModal(true)}
            onGarageClick={() => handleTabSelect('garage')}
            onVinClick={() => { setHeroMode('vin'); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
            onRequestPartClick={() => setShowRequestPartModal(true)}
            onAuthClick={() => router.push('/signup')}
          />
        )}

        {/* Global Floating Scroll-To-Top Button */}
        <ScrollToTopButton />

      </main>


      {/* ========================================================================= */}
      {/* MODAL 1: VOICE SEARCH MODAL (STITCH SCREEN 5) */}
      {/* ========================================================================= */}
      <VoiceSearchModal
        isOpen={isVoiceSearchOpen}
        onClose={() => setIsVoiceSearchOpen(false)}
        onSearch={handleVoiceSearchResult}
        activeVehicle={activeVehicle}
      />

      {/* ========================================================================= */}
      {/* MODAL 2: PRODUCT DETAIL MODAL (STITCH SCREEN 4) */}
      {/* ========================================================================= */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          isOpen={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onOpenVendorStore={(vendor) => {
            setSelectedProduct(null);
            handleOpenVendorStore(vendor);
          }}
          country={selectedCountry}
          activeVehicle={activeVehicle}
          apiBaseUrl={process.env.NEXT_PUBLIC_P4D_URL || 'https://parts4drive.com'}
        />
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: ADD VEHICLE TO VIRTUAL GARAGE */}
      {/* ========================================================================= */}
      {showAddCarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-100 dark:border-slate-800 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Car className="size-5 text-blue-600" />
                <h3 className="font-display font-bold text-base text-slate-900 dark:text-white">Add Vehicle to Garage</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddCarModal(false)}
                className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleAddVehicle} className="space-y-3.5">
              {/* NHTSA VIN Auto-Mapping Strip */}
              <div className="p-3 rounded-2xl bg-blue-50/70 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/50 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-bold text-[#0A1B4A] dark:text-blue-300 uppercase tracking-wider flex items-center gap-1.5">
                    <Search className="size-3.5 text-blue-600 dark:text-blue-400" />
                    Decode VIN &amp; Auto-Map Vehicle
                  </label>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">11-17 Chars</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={newCarVin}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase()
                      setNewCarVin(v)
                      if (v.length >= 11 && v.length <= 17) {
                        handleDecodeModalVin(v)
                      }
                    }}
                    placeholder="Enter VIN (e.g. 1HGCR2F89CA...)"
                    maxLength={17}
                    className="flex-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono text-slate-900 dark:text-white uppercase focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => handleDecodeModalVin()}
                    disabled={isDecodingModalVin || !newCarVin.trim()}
                    className="px-3 py-2 rounded-xl bg-[#0A1B4A] dark:bg-blue-600 hover:bg-[#0f2560] dark:hover:bg-blue-700 text-white font-semibold text-xs flex items-center gap-1 shrink-0 disabled:opacity-50 transition-colors"
                  >
                    {isDecodingModalVin ? (
                      <>
                        <Loader2 className="size-3.5 animate-spin" />
                        <span>Mapping...</span>
                      </>
                    ) : (
                      <>
                        <Search className="size-3.5" />
                        <span>Auto-Map</span>
                      </>
                    )}
                  </button>
                </div>
                {modalVinStatus && (
                  <p className={`text-[11px] font-semibold ${modalVinStatus.startsWith('✓') ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {modalVinStatus}
                  </p>
                )}
              </div>

              {/* Vehicle Make */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Vehicle Make (Catalog)</label>
                  <VehicleBrandLogo brand={newCarMake} size="xs" variant="badge" />
                </div>
                {/* Popular Brand Pills */}
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1.5 mb-1.5 scrollbar-none">
                  {['Toyota', 'Honda', 'Mercedes-Benz', 'Nissan', 'Hyundai', 'Ford', 'BMW', 'Volkswagen'].map((mk) => (
                    <button
                      key={mk}
                      type="button"
                      onClick={() => {
                        setNewCarMake(mk)
                        const foundMake = GLOBAL_MAKES.find((m) => m.name.toLowerCase() === mk.toLowerCase())
                        const models = foundMake?.models || []
                        if (models.length > 0) {
                          setNewCarModel(models[0])
                          setIsCustomModel(false)
                        } else {
                          setIsCustomModel(true)
                        }
                      }}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold shrink-0 border transition-all ${
                        newCarMake === mk
                          ? 'bg-blue-50 border-blue-600 text-blue-700 dark:bg-blue-950/60 dark:border-blue-400 dark:text-blue-300 shadow-xs'
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-blue-300'
                      }`}
                    >
                      <VehicleBrandLogo brand={mk} size="xs" variant="badge" />
                      <span>{mk}</span>
                    </button>
                  ))}
                </div>
                <select
                  value={newCarMake}
                  onChange={(e) => {
                    const makeVal = e.target.value
                    setNewCarMake(makeVal)
                    const foundMake = GLOBAL_MAKES.find((m) => m.name.toLowerCase() === makeVal.toLowerCase())
                    const models = foundMake?.models || []
                    if (models.length > 0) {
                      setNewCarModel(models[0])
                      setIsCustomModel(false)
                    } else {
                      setIsCustomModel(true)
                    }
                  }}
                  className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white font-medium"
                >
                  {GLOBAL_MAKES.map((m) => (
                    <option key={m.name} value={m.name}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Dynamic Vehicle Model */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Vehicle Model</label>
                  <button
                    type="button"
                    onClick={() => setIsCustomModel(!isCustomModel)}
                    className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline font-semibold"
                  >
                    {isCustomModel ? 'Select from list' : 'Type custom model'}
                  </button>
                </div>
                {isCustomModel ? (
                  <input
                    type="text"
                    value={newCarModel}
                    onChange={(e) => setNewCarModel(e.target.value)}
                    placeholder="e.g. Corolla, Camry, Land Cruiser"
                    required
                    className="w-full mt-1 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white"
                  />
                ) : (
                  <select
                    value={newCarModel}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setIsCustomModel(true)
                      } else {
                        setNewCarModel(e.target.value)
                      }
                    }}
                    className="w-full mt-1 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white font-medium"
                  >
                    {availableModelsForMake.map((mod) => (
                      <option key={mod} value={mod}>{mod}</option>
                    ))}
                    <option value="__custom__">+ Other / Custom Model...</option>
                  </select>
                )}
              </div>

              {/* Year & Trim */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Model Year</label>
                  <select
                    value={newCarYear}
                    onChange={(e) => setNewCarYear(e.target.value)}
                    className="w-full mt-1 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white font-medium"
                  >
                    {YEARS.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Vehicle Trim</label>
                  <input
                    type="text"
                    list="car-trims-datalist"
                    value={newCarTrim}
                    onChange={(e) => setNewCarTrim(e.target.value)}
                    placeholder="e.g. LE, XLE, Sport, GLI"
                    className="w-full mt-1 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white"
                  />
                  <datalist id="car-trims-datalist">
                    {availableTrimsForMake.map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Engine Specification (MMYTE) */}
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Engine Configuration (Catalog)</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <select
                    value={ENGINE_CONFIGURATIONS.includes(newCarEngine) ? newCarEngine : 'Custom'}
                    onChange={(e) => {
                      if (e.target.value !== 'Custom') {
                        setNewCarEngine(e.target.value)
                      }
                    }}
                    className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white font-medium"
                  >
                    {ENGINE_CONFIGURATIONS.map((eng) => (
                      <option key={eng} value={eng}>{eng}</option>
                    ))}
                    <option value="Custom">Custom / Other</option>
                  </select>
                  <input
                    type="text"
                    value={newCarEngine}
                    onChange={(e) => setNewCarEngine(e.target.value)}
                    placeholder="e.g. 1.8L I4 or 3.5L V6"
                    className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Car Summary Verification Badge */}
              <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex items-center justify-between">
                <div className="truncate">
                  <span className="text-[10px] text-slate-400 block uppercase font-bold">Car Details Profile</span>
                  <span className="text-xs font-bold text-[#0A1B4A] dark:text-white truncate block">
                    {newCarYear} {newCarMake} {newCarModel} {newCarTrim ? `(${newCarTrim})` : ''} • {newCarEngine}
                  </span>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 text-[10px] font-bold shrink-0 ml-2">
                  Car Fitment
                </span>
              </div>

              <button
                type="submit"
                className="w-full py-3 rounded-full bg-[#0A1B4A] hover:bg-[#12286b] dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-bold text-xs shadow-md transition-all mt-2 flex items-center justify-center gap-1.5"
              >
                <Car className="size-4" />
                <span>Save to Virtual Garage</span>
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 4: AUTH MODAL — renders the shared components/auth/LoginForm,      */}
      {/* i.e. the exact same sign-in implementation /login uses.                  */}
      {/* ========================================================================= */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-100 dark:border-slate-800 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src="/lightmodeicon.png" alt="Parts4Drive" width={24} height={24} className="size-6 object-contain dark:hidden shrink-0" />
                <img src="/darkmodeicon.png" alt="Parts4Drive" width={24} height={24} className="size-6 object-contain hidden dark:block shrink-0" />
                <h3 className="font-display font-bold text-base text-slate-900 dark:text-white">
                  Sign In to Parts4Drive
                </h3>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => {
                  setShowAuthModal(false)
                  setAuthNotice(null)
                  const isVerified = user && (user.email_verified === 1 || user.email_verified === true || user.emailVerified === true)
                  if (activeTab === 'garage' && !isVerified) {
                    setActiveTab('home')
                  }
                }}
                className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              >
                <X className="size-4" />
              </button>
            </div>

            {authNotice && (
              <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 text-xs text-blue-700 dark:text-blue-300">
                {authNotice}
              </div>
            )}

            <LoginForm onAuthed={handleAuthed} />
          </div>
        </div>
      )}

      {/* Email verification for an already-signed-in but unverified buyer
          (the same VerifyEmailDialog /login uses for device verification) */}
      <VerifyEmailDialog
        open={showVerifyDialog}
        onOpenChange={setShowVerifyDialog}
        email={user?.email || ''}
        purpose="email_verification"
        onSuccess={() => {
          setShowVerifyDialog(false)
          if (user) {
            setUser({ ...user, email_verified: 1, emailVerified: true })
          }
          setAuthNotice(null)
          setActiveTab('garage')
          if (pendingAddVehicle) {
            setShowAddCarModal(true)
            setPendingAddVehicle(false)
          }
        }}
      />

      {/* Barcode Scanner Modal for VIN */}
      {showBarcodeScanner && (
        <BarcodeScannerModal
          onClose={() => setShowBarcodeScanner(false)}
          onResult={(scannedVin) => {
            setVinInput(scannedVin)
            handleDecodeVin(scannedVin)
          }}
        />
      )}

      {/* Country / Market Selection Modal */}
      {showCountryModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#13161C] border border-slate-200 dark:border-slate-800 rounded-3xl p-6 w-full max-w-lg shadow-2xl space-y-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="size-9 rounded-full bg-[#0A1B4A]/10 text-[#0A1B4A] dark:bg-blue-600/20 dark:text-blue-400 flex items-center justify-center">
                  <Globe className="size-4" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-base text-[#0A1B4A] dark:text-white">
                    Select Regional Market
                  </h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Choose your country for localized inventory, pricing, and dispatch hubs
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCountryModal(false)}
                className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Group 1: Live Marketplaces */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-emerald-500"></span>
                <p className="text-xs font-bold text-[#0A1B4A] dark:text-emerald-400 uppercase tracking-wider">
                  Live Marketplaces (Immediate Same-Day Dispatch)
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {WEST_AFRICAN_COUNTRIES.filter(c => c.status === 'live').map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => {
                      handleCountrySelect(c.code)
                      setShowCountryModal(false)
                    }}
                    className={`flex items-center justify-between p-3 rounded-2xl border transition-all text-left ${
                      selectedCountry === c.code
                        ? 'bg-[#0A1B4A] text-white border-[#0A1B4A] shadow-sm'
                        : 'bg-slate-50 dark:bg-[#1a1f2c] border-slate-200/80 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:border-[#0A1B4A]/40'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-xl">{c.flag}</span>
                      <div>
                        <p className="font-bold text-xs">{c.name}</p>
                        <p className={`text-[10px] ${selectedCountry === c.code ? 'text-slate-200' : 'text-slate-500 dark:text-slate-400'}`}>
                          {c.currency} ({c.symbol})
                        </p>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                      Live
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Group 2: Expanding Corridor Hubs */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-blue-500"></span>
                <p className="text-xs font-bold text-[#0A1B4A] dark:text-blue-400 uppercase tracking-wider">
                  Expanding Corridor Hubs (Regional Cross-Border Network)
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {WEST_AFRICAN_COUNTRIES.filter(c => c.status === 'expanding').map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => {
                      handleCountrySelect(c.code)
                      setShowCountryModal(false)
                    }}
                    className={`flex items-center justify-between p-3 rounded-2xl border transition-all text-left ${
                      selectedCountry === c.code
                        ? 'bg-[#0A1B4A] text-white border-[#0A1B4A] shadow-sm'
                        : 'bg-slate-50 dark:bg-[#1a1f2c] border-slate-200/80 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:border-[#0A1B4A]/40'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-xl">{c.flag}</span>
                      <div>
                        <p className="font-bold text-xs">{c.name}</p>
                        <p className={`text-[10px] ${selectedCountry === c.code ? 'text-slate-200' : 'text-slate-500 dark:text-slate-400'}`}>
                          {c.currency} ({c.symbol})
                        </p>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-400">
                      Expanding
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Group 3: Coming Soon */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-slate-400"></span>
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Coming Soon (Request Sourcing &amp; Cross-Border Transit)
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {WEST_AFRICAN_COUNTRIES.filter(c => c.status === 'coming_soon').map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => {
                      handleCountrySelect(c.code)
                      setShowCountryModal(false)
                    }}
                    className={`flex items-center gap-2 p-2 rounded-xl border transition-all text-left ${
                      selectedCountry === c.code
                        ? 'bg-[#0A1B4A] text-white border-[#0A1B4A]'
                        : 'bg-slate-50 dark:bg-[#1a1f2c] border-slate-200/60 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-400'
                    }`}
                  >
                    <span className="text-base">{c.flag}</span>
                    <span className="font-medium text-[11px] truncate">{c.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Axon AI Concierge Trigger */}
      <button
        type="button"
        onClick={() => setShowAxonModal(true)}
        aria-label="Open Axon AI Fitment Concierge"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-xl shadow-blue-900/40 border border-blue-400/30 transition-all hover:scale-105 cursor-pointer"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
        </span>
        <div className="size-5 rounded-full overflow-hidden border border-white/20">
          <img src="/logo/axon-ai.png" alt="Axon AI" className="size-full object-cover" />
        </div>
        <span>Axon Concierge</span>
      </button>

      {/* Axon AI Slide-Over Concierge Drawer */}
      {showAxonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/60 backdrop-blur-xs p-2 sm:p-4">
          <div className="relative w-full max-w-2xl h-[92vh] max-h-[900px] flex flex-col rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#13161C] shadow-2xl overflow-hidden animate-in slide-in-from-right duration-300">
            {/* Header with Close */}
            <div className="p-3.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#10131A] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <div className="size-8 rounded-xl overflow-hidden border border-blue-500/20 bg-blue-900/10 flex items-center justify-center">
                  <img src="/logo/axon-ai.png" alt="Axon AI" className="size-full object-cover" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-slate-900 dark:text-white">Axon AI Diagnostic Concierge</h3>
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                    <span className="size-1.5 rounded-full bg-emerald-500"></span> Online &amp; Fitment Verified
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAxonModal(false)}
                className="size-8 rounded-full bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors cursor-pointer"
                aria-label="Close Axon AI Concierge"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Chat Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0 scroll-smooth">
              {chatMessages.length === 0 ? (
                <div className="h-full flex flex-col justify-center items-center py-6 px-3 text-center max-w-md mx-auto space-y-4">
                  <div className="size-14 rounded-2xl overflow-hidden border border-blue-500/20 bg-blue-900/10 flex items-center justify-center shadow-md p-1">
                    <img src="/logo/axon-ai.png" alt="Axon AI Specialist" className="size-full object-cover rounded-xl" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-bold text-base text-slate-900 dark:text-white">Axon AI Specialist</h4>
                    <p className="text-xs text-slate-500 dark:text-zinc-400">
                      Ask about exact car fitment, VIN decoding, or diagnostic sound analysis.
                    </p>
                  </div>
                  <div className="w-full space-y-2 pt-2">
                    {[
                      "Check front brake pads for 2018 Toyota Camry",
                      "Decode European VIN: WVWZZZ3CZGE110616",
                      "Diagnose squeaking suspension over bumps"
                    ].map((chip) => (
                      <button
                        key={chip}
                        type="button"
                        onClick={() => handleSendMessage(chip)}
                        className="w-full px-3 py-2 rounded-xl text-xs font-medium bg-slate-100 dark:bg-[#181C24] hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:text-blue-600 text-left border border-slate-200 dark:border-slate-800 transition-colors cursor-pointer"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {chatMessages.map((msg) => {
                    const isUser = msg.sender === 'user';
                    return (
                      <div key={msg.id} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-full`}>
                        <div
                          className={`p-3 rounded-2xl max-w-[85%] text-xs leading-relaxed whitespace-pre-wrap break-words ${
                            isUser
                              ? 'bg-blue-600 text-white font-medium rounded-tr-none shadow-sm'
                              : 'bg-slate-100 dark:bg-[#181C24] text-slate-900 dark:text-zinc-200 rounded-tl-none border border-slate-200 dark:border-slate-800'
                          }`}
                        >
                          {msg.content}
                        </div>
                      </div>
                    );
                  })}
                  {chatLoading && (
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 p-2">
                      <Loader2 className="size-4 animate-spin text-blue-600" />
                      <span>Axon is analyzing part tolerances...</span>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </>
              )}
            </div>

            {/* Chat Input */}
            <div className="p-3 bg-slate-50 dark:bg-[#10131A] border-t border-slate-200 dark:border-slate-800 shrink-0">
              <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex items-center gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask Axon about fitment, parts or VIN..."
                  className="flex-1 bg-white dark:bg-[#181C24] border border-slate-200 dark:border-slate-800 rounded-xl px-3.5 py-2 text-xs text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-hidden focus:border-blue-500"
                />
                <button
                  type="submit"
                  disabled={!chatInput.trim() || chatLoading}
                  className="px-3.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs disabled:opacity-40 transition-colors shrink-0 cursor-pointer"
                >
                  <Send className="size-3.5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Request a Part Demand Capture Modal */}
      <RequestPartModal
        isOpen={showRequestPartModal}
        onClose={() => setShowRequestPartModal(false)}
        initialVehicle={activeVehicle ? {
          make: activeVehicle.make,
          model: activeVehicle.model,
          year: activeVehicle.year
        } : null}
        country={selectedCountry}
      />
    </div>
  )
}
